/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { flushSync, createPortal } from 'react-dom';
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Checkbox,
  TablePagination,
} from '@mui/material';
import MonacoEditorWithSearch from '../components/MonacoEditorWithSearch';
import { getSyntaxHighlightingLanguage, validateAndFormatContent, stripFunctionCallsForJsonValidation } from '../utils/contentValidation';
import { formatLastExecutedTimestamp } from '../utils/dateTimeUtils';
import { Save as SaveIcon, Delete as DeleteIcon, DeleteSweep as DeleteSweepIcon, PlayArrow as PlayArrowIcon, FileDownload as FileDownloadIcon, FileUpload as FileUploadIcon, ContentCopy as ContentCopyIcon, Visibility as VisibilityIcon, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import VerticalViewModal from './VerticalViewModal';
import { RequestDataSet } from '../types/dataSets';
import { apiService } from '../services/apiService';
import { environmentPlaceholderSx } from '../utils/environmentUtils';

const MAX_BULK_GENERATE_COUNT = 1000;
const MIN_BULK_GENERATE_COUNT = 1;
/** Above this row count, only render visible rows and defer JSON parsing per row. */
const LARGE_DATA_SET_TABLE_THRESHOLD = 80;
const VIRTUAL_ROW_HEIGHT_PX = 42;
const VIRTUAL_OVERSCAN_ROWS = 5;
const PARSE_DATA_SETS_CHUNK_SIZE = 50;

/** Restrict bulk count field to digits only, clamped to 0–1000. */
function clampBulkCountInput(raw: string): string {
  const digitsOnly = raw.replace(/\D/g, '');
  if (digitsOnly === '') return '';
  const num = parseInt(digitsOnly, 10);
  if (Number.isNaN(num)) return '';
  return String(Math.min(MAX_BULK_GENERATE_COUNT, Math.max(0, num)));
}

const MAX_AI_TEST_CASE_COUNT = 20;

/** Restrict AI positive/negative count fields to digits only, clamped to 0–20. */
function clampAiTestCaseCountInput(raw: string): string {
  const digitsOnly = raw.replace(/\D/g, '');
  if (digitsOnly === '') return '';
  const num = parseInt(digitsOnly, 10);
  if (Number.isNaN(num)) return '';
  return String(Math.min(MAX_AI_TEST_CASE_COUNT, Math.max(0, num)));
}

/** Merge parsed JSON with row metadata. Spreading an array into `{...}` yields numeric keys (`{"0":...}`). */
export function mergeParsedBodyWithDataSetMeta(
  parsed: unknown,
  meta: Record<string, unknown>,
  originalBody: string
): Record<string, unknown> {
  if (Array.isArray(parsed)) {
    return { ...meta, __rawBody: originalBody };
  }
  if (parsed !== null && typeof parsed === 'object') {
    const cleaned = cleanDataForDisplay(parsed);
    if (Array.isArray(cleaned)) {
      return { ...meta, __rawBody: originalBody };
    }
    return { ...normalizeBodyObjectForRow(cleaned), ...meta };
  }
  return { ...meta, __rawBody: originalBody };
}

/** Undo accidental persistence of lazy-row metadata as JSON `{"__lazyBody":"..."}` (can nest). */
export function unwrapLazyBodyWrapper(body: string): string {
  let current = (body ?? '').trim();
  if (!current) return current;
  for (let depth = 0; depth < 8; depth++) {
    try {
      const parsed = JSON.parse(current);
      if (
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        Object.keys(parsed).length === 1 &&
        typeof (parsed as Record<string, unknown>).__lazyBody === 'string'
      ) {
        current = String((parsed as Record<string, unknown>).__lazyBody).trim();
        continue;
      }
    } catch {
      break;
    }
    break;
  }
  return current;
}

const TABLE_ROW_META_KEYS = new Set([
  '_savedDataSetId', '_savedDataSetIndex', '__lazyBody', '__rawBody', '__textTemplate',
]);

/** Unique {{variable}} names in template order (Text/HTML/XML/JS bodies). */
export function extractVariablePlaceholderKeys(text: string): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const key = String(match[1]).trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      keys.push(key);
    }
  }
  return keys;
}

/** Substitute {{var}} placeholders in a text template using flat row values. */
export function applyRowValuesToTextTemplate(
  template: string,
  values: Record<string, unknown>
): string {
  return template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, varName) => {
    const key = String(varName).trim();
    if (!key) return match;
    const val = values[key];
    return val === undefined || val === null ? '' : String(val);
  });
}

/** Reverse-fill variable values from a saved text body using the request template. */
export function extractRowValuesFromTextTemplate(
  template: string,
  body: string
): Record<string, string> {
  const values: Record<string, string> = {};
  const parts: Array<{ type: 'text' | 'var'; value: string }> = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(template)) !== null) {
    parts.push({ type: 'text', value: template.slice(cursor, match.index) });
    parts.push({ type: 'var', value: String(match[1]).trim() });
    cursor = match.index + match[0].length;
  }
  parts.push({ type: 'text', value: template.slice(cursor) });
  if (!parts.some((p) => p.type === 'var')) return values;

  let regexStr = '^';
  for (const part of parts) {
    if (part.type === 'text') {
      regexStr += part.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    } else {
      regexStr += '(.*?)';
    }
  }
  regexStr += '$';
  try {
    const bodyMatch = body.match(new RegExp(regexStr, 's'));
    if (!bodyMatch) return values;
    let cap = 1;
    for (const part of parts) {
      if (part.type === 'var') {
        values[part.value] = bodyMatch[cap] ?? '';
        cap++;
      }
    }
  } catch {
    // ignore regex errors
  }
  return values;
}

/** HTTP body string for execute/save from a data table row (preserves top-level JSON arrays). */
export function getRequestBodyFromTableRow(row: any, cleanedRowData?: unknown): string {
  const textTemplate =
    row != null && typeof row.__textTemplate === 'string' ? row.__textTemplate : null;
  if (textTemplate && extractVariablePlaceholderKeys(textTemplate).length > 0) {
    const fieldMap =
      cleanedRowData !== undefined &&
      cleanedRowData &&
      typeof cleanedRowData === 'object' &&
      !Array.isArray(cleanedRowData)
        ? extractFlatFieldMapFromObject(cleanedRowData as Record<string, unknown>)
        : extractFlatFieldMapFromRow(row);
    if (Object.keys(fieldMap).some((k) => !TABLE_ROW_META_KEYS.has(k))) {
      return applyRowValuesToTextTemplate(textTemplate, fieldMap);
    }
  }
  if (row != null && typeof row.__rawBody === 'string') {
    const unwrapped = unwrapLazyBodyWrapper(row.__rawBody);
    if (!unwrapped.trim() || unwrapped.trim() === '{}') return '';
    return unwrapped;
  }
  // Large tables (>80 rows) defer JSON parse; persist the original body string, not a wrapper object.
  if (row != null && typeof row.__lazyBody === 'string') {
    const unwrapped = unwrapLazyBodyWrapper(row.__lazyBody);
    if (!unwrapped.trim() || unwrapped.trim() === '{}') return '';
    return unwrapped;
  }
  const payload = cleanedRowData !== undefined ? cleanedRowData : row;
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const nested =
      cleanedRowData !== undefined
        ? unflattenFlatKeys(extractFlatFieldMapFromObject(payload as Record<string, unknown>))
        : rowToNestedStorage(row);
    const keys = Object.keys(nested).filter((k) => !TABLE_ROW_META_KEYS.has(k));
    if (keys.length === 0) return '';
    const serialized = JSON.stringify(nested, null, 2);
    return serialized === '{}' ? '' : serialized;
  }
  return JSON.stringify(payload, null, 2);
}

function parseDataSetToTableRow(
  ds: RequestDataSet,
  index: number,
  lazyParse = false,
  textTemplate?: string
): any {
  const meta: any = { _savedDataSetIndex: index };
  if (ds.id != null && Number(ds.id) > 0) {
    meta._savedDataSetId = ds.id;
  }
  if (!ds.body) return textTemplate ? { ...meta, __textTemplate: textTemplate } : meta;
  const bodyStr = unwrapLazyBodyWrapper(String(ds.body));
  if (!bodyStr.trim() || bodyStr.trim() === '{}') {
    return textTemplate ? { ...meta, __textTemplate: textTemplate } : meta;
  }
  if (lazyParse) {
    return textTemplate
      ? { ...meta, __lazyBody: bodyStr, __textTemplate: textTemplate }
      : { ...meta, __lazyBody: bodyStr };
  }
  try {
    const parsed = JSON.parse(bodyStr);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const keys = Object.keys(parsed);
      if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
        const arr = keys.sort((a, b) => Number(a) - Number(b)).map((k) => parsed[k as keyof typeof parsed]);
        return { ...meta, __rawBody: JSON.stringify(arr, null, 2) };
      }
    }
    return mergeParsedBodyWithDataSetMeta(parsed, meta, bodyStr);
  } catch {
    const template = textTemplate ?? '';
    const vars = template ? extractVariablePlaceholderKeys(template) : [];
    if (template && vars.length > 0) {
      const extracted = extractRowValuesFromTextTemplate(template, bodyStr);
      if (Object.keys(extracted).length > 0) {
        return { ...extracted, __textTemplate: template, ...meta };
      }
      return { ...meta, __rawBody: bodyStr, __textTemplate: template };
    }
    return { ...meta, __rawBody: bodyStr };
  }
}

/** Parse row for display; lazy rows only parse when rendered (visible window). */
function getFlatRowForDisplay(row: any): Record<string, any> {
  if (!row || typeof row !== 'object') return {};
  if (row.__lazyBody != null) {
    const meta = {
      _savedDataSetId: row._savedDataSetId,
      _savedDataSetIndex: row._savedDataSetIndex,
    };
    const bodyStr = unwrapLazyBodyWrapper(String(row.__lazyBody));
    try {
      const parsed = JSON.parse(bodyStr);
      if (Array.isArray(parsed)) {
        return { ...meta, __rawBody: bodyStr };
      }
      const cleaned = cleanDataForDisplay(parsed);
      return { ...flatten(cleaned), ...meta };
    } catch {
      const template = typeof row.__textTemplate === 'string' ? row.__textTemplate : '';
      if (template) {
        const extracted = extractRowValuesFromTextTemplate(template, bodyStr);
        if (Object.keys(extracted).length > 0) {
          return { ...extracted, ...meta, __textTemplate: template };
        }
      }
      return { ...meta, __rawBody: bodyStr };
    }
  }
  const meta = {
    _savedDataSetId: row._savedDataSetId,
    _savedDataSetIndex: row._savedDataSetIndex,
  };
  return { ...extractFlatFieldMapFromRow(row), ...meta };
}

/** Flat field map for table display / serialization; nested paths win over empty flat placeholders. */
function isPlaceholderFlatValue(value: unknown): boolean {
  if (value === '') return true;
  return Array.isArray(value) && value.length === 0;
}

function extractFlatFieldMapFromRow(row: Record<string, unknown>): Record<string, unknown> {
  const fields: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(row)) {
    if (TABLE_ROW_META_KEYS.has(key) || key === '__rawBody' || key.includes('.')) continue;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const nestedFlat = flatten(cleanDataForDisplay(value), key);
      for (const [k, v] of Object.entries(nestedFlat)) {
        fields[k] = v;
      }
    } else {
      fields[key] = value;
    }
  }

  for (const [key, value] of Object.entries(row)) {
    if (TABLE_ROW_META_KEYS.has(key) || key === '__rawBody' || !key.includes('.')) continue;
    const existing = fields[key];
    if (existing === undefined) {
      fields[key] = value;
    } else if (!isPlaceholderFlatValue(value)) {
      fields[key] = value;
    } else if (isPlaceholderFlatValue(existing)) {
      fields[key] = value;
    }
  }

  return fields;
}

const MODAL_INTERNAL_ROW_KEYS = new Set([
  '_savedDataSetId', '_savedDataSetIndex', '__lazyBody', '__rawBody',
  'id', 'ID', 'dataSetId', 'DataSetId',
]);

export function buildRowFromModalSave(
  originalRow: any | null,
  editedFlat: Record<string, any>,
  templateTypes?: Map<string, string> | null
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const savedId = originalRow?._savedDataSetId ?? editedFlat?._savedDataSetId;
  const savedIndex = originalRow?._savedDataSetIndex ?? editedFlat?._savedDataSetIndex;
  if (savedId != null) payload._savedDataSetId = savedId;
  if (savedIndex != null) payload._savedDataSetIndex = savedIndex;
  // Text / non-JSON raw bodies are edited via the __rawBody field in View Details.
  if (typeof editedFlat.__rawBody === 'string') {
    return { ...payload, __rawBody: editedFlat.__rawBody };
  }
  const flatOriginal = originalRow ? getFlatRowForDisplay(originalRow) : {};
  const fieldMap: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(editedFlat)) {
    if (MODAL_INTERNAL_ROW_KEYS.has(key)) continue;
    const previousValue = flatOriginal[key];
    fieldMap[key] =
      typeof val === 'string'
        ? coerceCellEditValue(
            resolveCellOriginalType(key, previousValue, templateTypes),
            previousValue,
            val
          )
        : val;
  }
  const nested = unflattenFlatKeys(fieldMap);
  if (
    typeof originalRow?.__rawBody === 'string' &&
    Object.keys(nested).length === 0
  ) {
    return { ...payload, __rawBody: originalRow.__rawBody };
  }
  const result = { ...nested, ...payload };
  if (typeof originalRow?.__textTemplate === 'string') {
    result.__textTemplate = originalRow.__textTemplate;
  }
  return result;
}

function tableRowHasContent(row: any): boolean {
  if (!row || typeof row !== 'object') return false;
  if (typeof row.__rawBody === 'string' && row.__rawBody.trim() !== '') return true;
  if (typeof row.__lazyBody === 'string' && row.__lazyBody.trim() !== '') return true;
  return Object.keys(row).some((key) => {
    if (TABLE_ROW_META_KEYS.has(key)) return false;
    const value = row[key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
}

function tableRowContentSignature(row: any): string {
  if (!row || typeof row !== 'object') return '';
  if (typeof row.__rawBody === 'string') return `raw:${row.__rawBody}`;
  if (typeof row.__lazyBody === 'string') return `raw:${row.__lazyBody}`;
  const cleaned: Record<string, unknown> = {};
  for (const key of Object.keys(row).sort()) {
    if (TABLE_ROW_META_KEYS.has(key)) continue;
    cleaned[key] = row[key];
  }
  try {
    return JSON.stringify(cleaned);
  } catch {
    return '';
  }
}

/** Canonical body signature for deduping imported rows against persisted data sets. */
function tableRowPersistedBodySignature(row: any): string {
  if (!row || typeof row !== 'object') return '';
  try {
    const cleaned: Record<string, unknown> = {};
    for (const key of Object.keys(row)) {
      if (TABLE_ROW_META_KEYS.has(key)) continue;
      cleaned[key] = row[key];
    }
    const body = getRequestBodyFromTableRow(row, cleaned).trim();
    if (!body) return '';
    try {
      return JSON.stringify(JSON.parse(body));
    } catch {
      return body;
    }
  } catch {
    return tableRowContentSignature(row);
  }
}

function tableRowsSemanticallyEqual(a: any[], b: any[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?._savedDataSetId !== b[i]?._savedDataSetId) return false;
    if (a[i]?._savedDataSetIndex !== b[i]?._savedDataSetIndex) return false;
    if (tableRowContentSignature(a[i]) !== tableRowContentSignature(b[i])) return false;
  }
  return true;
}

function buildCellEditResult(
  prev: any[],
  rowIdx: number,
  key: string,
  value: unknown,
  originalTypes: Map<string, string>,
  templateTypes?: Map<string, string> | null
): { arr: any[]; targetIdx: number; updatedRow: any; selectUnsaved: boolean } | null {
  if (rowIdx >= prev.length) return null;

  const arr = [...prev];
  const targetIdx = rowIdx;
  const currentRow = arr[rowIdx];
  const savedDataSetId = currentRow?._savedDataSetId;
  const savedDataSetIndex = currentRow?._savedDataSetIndex;

  let baseRow = rowToNestedStorage(currentRow || {});
  const previousValue = getValueAtPath(baseRow, key);
  const editKey = `${rowIdx}\0${key}`;
  const originalType =
    originalTypes.get(editKey) ?? templateTypes?.get(key);
  const newValue =
    typeof value === 'string'
      ? coerceCellEditValue(originalType, previousValue, value)
      : value;

  const updatedRow: Record<string, unknown> = { ...baseRow };
  setValueAtPath(updatedRow, key, newValue);
  stripLiteralDotNotationKeys(updatedRow);
  if (key !== '__rawBody') {
    delete updatedRow.__rawBody;
    delete updatedRow.__lazyBody;
  }
  if (savedDataSetId != null) updatedRow._savedDataSetId = savedDataSetId;
  if (savedDataSetIndex != null) updatedRow._savedDataSetIndex = savedDataSetIndex;
  if (typeof currentRow?.__textTemplate === 'string') {
    updatedRow.__textTemplate = currentRow.__textTemplate;
  }
  arr[targetIdx] = updatedRow;

  return {
    arr,
    targetIdx,
    updatedRow,
    selectUnsaved: !!(currentRow && !currentRow._savedDataSetId),
  };
}

function resolveDataArrRowIndex(rows: any[], row: any | null, explicitIdx?: number | null): number {
  if (!row) {
    return typeof explicitIdx === 'number' && explicitIdx >= 0 ? explicitIdx : -1;
  }
  const byRef = rows.findIndex((r) => r === row);
  if (byRef >= 0) return byRef;
  const id = row._savedDataSetId;
  if (id != null) {
    const byId = rows.findIndex((r) => r?._savedDataSetId === id);
    if (byId >= 0) return byId;
  }
  if (
    typeof explicitIdx === 'number' &&
    explicitIdx >= 0 &&
    explicitIdx < rows.length &&
    (rows[explicitIdx] === row ||
      (id != null && rows[explicitIdx]?._savedDataSetId === id) ||
      (id == null &&
        row._savedDataSetIndex != null &&
        rows[explicitIdx]?._savedDataSetIndex === row._savedDataSetIndex))
  ) {
    return explicitIdx;
  }
  if (
    typeof row._savedDataSetIndex === 'number' &&
    row._savedDataSetIndex >= 0 &&
    row._savedDataSetIndex < rows.length
  ) {
    return row._savedDataSetIndex;
  }
  if (typeof explicitIdx === 'number' && explicitIdx >= 0 && explicitIdx < rows.length) {
    return explicitIdx;
  }
  return -1;
}

function flatten(obj: any, prefix = '', res: any = {}) {
  for (const key in obj) {
    const val = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      flatten(val, newKey, res);
    } else if (Array.isArray(val)) {
      res[newKey] = val; // treat arrays as leaf
    } else if (val === null || typeof val !== 'object') {
      // JSON null is a real field (e.g. "username": null) and must still be a column.
      res[newKey] = val;
    }
  }
  return res;
}

/** Remove accidental `metadata.env`-style keys after nesting edits. */
function stripLiteralDotNotationKeys(obj: Record<string, unknown>) {
  for (const key of Object.keys(obj)) {
    if (key.includes('.') && !TABLE_ROW_META_KEYS.has(key)) {
      delete obj[key];
    }
  }
}

function extractFlatFieldMapFromObject(obj: Record<string, unknown>): Record<string, unknown> {
  return extractFlatFieldMapFromRow(obj);
}

/** Convert dot-notation flat keys (e.g. metadata.env) into nested JSON objects. */
export function unflattenFlatKeys(flat: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const dotEntries: [string, unknown][] = [];
  const simpleEntries: [string, unknown][] = [];

  for (const [key, value] of Object.entries(flat)) {
    if (TABLE_ROW_META_KEYS.has(key) || key === '__rawBody') continue;
    if (key.includes('.')) dotEntries.push([key, value]);
    else simpleEntries.push([key, value]);
  }

  const topLevelFromDots = new Set<string>();
  for (const [key] of dotEntries) {
    topLevelFromDots.add(key.split('.')[0]!);
  }

  for (const [key, value] of dotEntries) {
    setValueAtPath(result, key, value);
  }

  for (const [key, value] of simpleEntries) {
    if (topLevelFromDots.has(key)) continue;
    const existing = result[key];
    if (existing != null && typeof existing === 'object' && !Array.isArray(existing)) {
      continue;
    }
    result[key] = value;
  }

  return result;
}

function normalizeBodyObjectForRow(parsed: unknown): Record<string, unknown> {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  return unflattenFlatKeys(extractFlatFieldMapFromObject(parsed as Record<string, unknown>));
}

function rowToNestedStorage(row: any): Record<string, unknown> {
  if (!row || typeof row !== 'object') return {};
  return unflattenFlatKeys(extractFlatFieldMapFromRow(row));
}

function setValueAtPath(obj: any, path: string, value: any) {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  let target = obj;
  for (const key of keys) {
    if (!target[key] || typeof target[key] !== 'object') target[key] = {};
    target = target[key];
  }
  target[lastKey] = value;
  return obj;
}

function getValueAtPath(obj: any, path: string): unknown {
  if (!obj || typeof path !== 'string' || !path) return undefined;
  const keys = path.split('.');
  let current: any = obj;
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[key];
  }
  return current;
}

/**
 * Preserve JSON types when editing via <input> (which only yields strings).
 * Uses the type captured on focus so mid-typing partial values stay strings until complete.
 */
export function coerceCellEditValue(
  originalType: string | undefined,
  previousValue: unknown,
  rawInput: string
): unknown {
  if (typeof rawInput !== 'string') return rawInput;
  const trimmed = rawInput.trim();
  const lower = trimmed.toLowerCase();

  const coerceBoolean =
    originalType === 'boolean' || typeof previousValue === 'boolean';
  if (coerceBoolean) {
    if (lower === 'true') return true;
    if (lower === 'false') return false;
    return rawInput;
  }

  const coerceNumber =
    originalType === 'number' || typeof previousValue === 'number';
  if (coerceNumber) {
    if (trimmed === '') return '';
    const n = Number(trimmed);
    if (!Number.isNaN(n) && Number.isFinite(n)) return n;
    return rawInput;
  }

  if (previousValue === null && lower === 'null') return null;

  const coerceArray = originalType === 'array' || Array.isArray(previousValue);
  if (coerceArray) {
    if (trimmed === '') return [];
    const parsed = tryParseArrayCellValue(trimmed);
    if (parsed != null) return parsed;
    return rawInput;
  }

  return rawInput;
}

/** Leaf field types from a single request body JSON string. */
export function getRequestBodyLeafTypes(bodyText: string): Map<string, string> {
  const types = new Map<string, string>();
  const normalized = unwrapLazyBodyWrapper((bodyText ?? '').trim());
  if (!normalized) return types;
  try {
    const parsed = JSON.parse(normalized);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return types;
    const flat = flatten(parsed);
    for (const [key, val] of Object.entries(flat)) {
      if (val === null) types.set(key, 'null');
      else if (Array.isArray(val)) types.set(key, 'array');
      else types.set(key, typeof val);
    }
  } catch {
    // Invalid / non-JSON bodies: {{var}} is for execution substitution only — not Data Table columns.
  }
  return types;
}

function leafTypePriority(type: string): number {
  switch (type) {
    case 'number':
    case 'boolean':
      return 4;
    case 'null':
      return 3;
    case 'array':
      return 2;
    case 'string':
      return 1;
    default:
      return 0;
  }
}

/** Merge leaf types from multiple bodies; number/boolean beat string for the same key. */
export function mergeRequestBodyLeafTypes(bodyTexts: string[]): Map<string, string> {
  const merged = new Map<string, string>();
  for (const bodyText of bodyTexts) {
    const types = getRequestBodyLeafTypes(bodyText);
    types.forEach((type, key) => {
      const prev = merged.get(key);
      if (!prev || leafTypePriority(type) > leafTypePriority(prev)) {
        merged.set(key, type);
      }
    });
  }
  return merged;
}

function collectRequestBodyTextCandidates(
  value: string | undefined,
  editorValue: string,
  latestEditorValue: string | null | undefined,
  allDataSets: RequestDataSet[] | undefined
): string[] {
  const candidates: string[] = [value ?? '', latestEditorValue ?? '', editorValue ?? ''];
  for (const ds of allDataSets ?? []) {
    if (ds?.body != null) {
      candidates.push(unwrapLazyBodyWrapper(String(ds.body)));
    }
  }
  return candidates.filter((t) => {
    const trimmed = (t ?? '').trim();
    return trimmed !== '' && trimmed !== '{}';
  });
}

export function resolveCellOriginalType(
  key: string,
  val: unknown,
  templateTypes?: Map<string, string> | null
): string {
  if (val === null) return 'null';
  if (Array.isArray(val)) return 'array';
  if (typeof val === 'boolean' || typeof val === 'number') return typeof val;
  const templateType = templateTypes?.get(key);
  if (
    templateType === 'number' ||
    templateType === 'boolean' ||
    templateType === 'null' ||
    templateType === 'array'
  ) {
    return templateType;
  }
  return typeof val === 'string' ? 'string' : typeof val;
}

/** Build an empty JSON value that preserves object/array nesting from a sample payload. */
export function buildEmptyJsonTemplate(value: unknown): unknown {
  if (value === null) return null;
  if (Array.isArray(value)) return [];
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      out[key] = buildEmptyJsonTemplate(child);
    }
    return out;
  }
  if (typeof value === 'boolean') return '';
  if (typeof value === 'number') return '';
  return '';
}

/** Parse body text and return JSON string with the same nested shape but empty leaf values. */
export function buildEmptyBodyTemplateFromText(bodyText: string): string {
  const normalized = unwrapLazyBodyWrapper((bodyText ?? '').trim());
  if (!normalized) return '';
  try {
    const parsed = JSON.parse(normalized);
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return '';
    const empty = buildEmptyJsonTemplate(parsed);
    const serialized = JSON.stringify(empty);
    return serialized === '{}' ? '' : serialized;
  } catch {
    // Text/HTML/XML/JS: keep template with {{variables}} for new data sets
    return normalized;
  }
}

/**
 * Parse request body and return Data Table column keys from valid JSON fields only.
 * {{variable}} placeholders must NOT become columns for Text/HTML/XML/JS or invalid JSON;
 * they remain available for execution-time variable substitution elsewhere.
 */
function getRequestBodyColumnKeys(value: string, rawBodySubtype: string = 'JSON'): string[] {
  if (!value || typeof value !== 'string') return [];
  const normalized = unwrapLazyBodyWrapper(value.trim());
  if (!normalized) return [];
  // Structured Data Table columns are JSON-body only.
  if (String(rawBodySubtype || 'JSON').toUpperCase() !== 'JSON') {
    return [];
  }
  try {
    const parsed = JSON.parse(normalized);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
    const flat = flatten(parsed);
    const keys: string[] = [];
    for (const key of Object.keys(flat)) {
      if (
        key === '_savedDataSetId' ||
        key === '_savedDataSetIndex' ||
        key === '__lazyBody' ||
        key === '__rawBody' ||
        key === '__textTemplate'
      ) {
        continue;
      }
      const val = flat[key];
      if (val !== null && typeof val === 'object' && !Array.isArray(val)) continue; // skip nested objects
      keys.push(key);
    }
    return keys;
  } catch {
    // Invalid JSON: do not invent columns from {{placeholders}}.
    return [];
  }
}

/** Best-effort request body text for deriving data-table columns (value prop, editor, or saved rows). */
function resolveRequestBodyTextForColumns(
  value: string | undefined,
  editorValue: string,
  latestEditorValue: string | null | undefined,
  allDataSets: RequestDataSet[] | undefined,
  rawBodySubtype: string = 'JSON'
): string {
  if (String(rawBodySubtype || 'JSON').toUpperCase() !== 'JSON') {
    return '';
  }
  const candidates = collectRequestBodyTextCandidates(
    value,
    editorValue,
    latestEditorValue,
    allDataSets
  );
  let best = '';
  let bestKeyCount = 0;
  for (const t of candidates) {
    const keyCount = getRequestBodyColumnKeys(t, rawBodySubtype).length;
    if (keyCount > bestKeyCount) {
      bestKeyCount = keyCount;
      best = t;
    }
  }
  return best;
}

// Helper function to remove internal metadata fields from data
function cleanDataForDisplay(data: any): any {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) {
    return data.map(cleanDataForDisplay);
  }
  const cleaned: any = {};
  for (const key in data) {
    // Skip internal metadata fields
    if (
      key !== '_savedDataSetId' &&
      key !== '_savedDataSetIndex' &&
      key !== '__lazyBody' &&
      key !== '__rawBody' &&
      key !== '__textTemplate'
    ) {
      cleaned[key] = typeof data[key] === 'object' ? cleanDataForDisplay(data[key]) : data[key];
    }
  }
  return cleaned;
}

/** Parse array cell text; returns null when JSON is incomplete/invalid (do not reset to []). */
export function tryParseArrayCellValue(text: string): unknown[] | null {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    // Single object pasted into an array column → treat as one-element array.
    if (parsed !== null && typeof parsed === 'object') return [parsed];
    return null;
  } catch {
    // Allow `{1,2,[3,4]}`-style input (outer braces instead of array brackets).
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const asArray = JSON.parse(`[${trimmed.slice(1, -1)}]`);
        if (Array.isArray(asArray)) return asArray;
      } catch {
        // fall through
      }
    }
    return null;
  }
}

interface ArrayCellInputProps {
  rowIdx: number;
  columnKey: string;
  value: unknown[];
  onChange: (rowIdx: number, key: string, value: unknown[]) => void;
  onEditStart: () => void;
  onEditEnd: () => void;
}

function ArrayCellInput({
  rowIdx,
  columnKey,
  value,
  onChange,
  onEditStart,
  onEditEnd,
}: ArrayCellInputProps) {
  const [draft, setDraft] = React.useState(() => JSON.stringify(value));
  const isEditingRef = React.useRef(false);

  React.useEffect(() => {
    if (!isEditingRef.current) {
      setDraft(JSON.stringify(value));
    }
  }, [value]);

  const strVal = draft;
  return (
    <Tooltip title={strVal} arrow>
      <input
        value={strVal}
        style={{
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          border: 'none',
          background: 'transparent',
          outline: 'none',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
        }}
        onFocus={() => {
          isEditingRef.current = true;
          setDraft(JSON.stringify(value));
          onEditStart();
        }}
        onBlur={() => {
          // Commit before clearing edit flags so Save/auto-save see the latest value.
          const parsed = tryParseArrayCellValue(draft);
          if (parsed != null) {
            onChange(rowIdx, columnKey, parsed);
            setDraft(JSON.stringify(parsed));
          } else {
            setDraft(JSON.stringify(value));
          }
          isEditingRef.current = false;
          onEditEnd();
        }}
        onChange={(e) => {
          const text = e.target.value;
          setDraft(text);
          const parsed = tryParseArrayCellValue(text);
          if (parsed != null) {
            onChange(rowIdx, columnKey, parsed);
          }
        }}
      />
    </Tooltip>
  );
}

interface RawJsonTableProps {
  value?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onSave?: (rowData: any, rowIndex: number) => void | Promise<void>;
  onDelete?: (rowIndex: number, dataSetId?: number) => void; // Pass dataSetId if row is saved
  /** Optional: delete multiple data sets at once. When provided, checkboxes and "Delete selected" are shown. */
  onDeleteMultiple?: (items: { rowIndex: number; dataSetId?: number }[]) => Promise<void>;
  onExecute?: (rowData: any, rowIndex: number) => void;
  // Milestone 2: prefer emitting intent by ID (faster, avoids passing big row objects).
  onExecuteDataSetId?: (dataSetId: number) => void;
  onDataSetSelect?: (dataSet: RequestDataSet) => void; // Callback when a data set row is selected
  selectedDataSetId?: number; // ID of the currently selected data set
  requestId?: number; // Optional request ID to enable/disable save button
  requestMethod?: string; // HTTP method for the owning request (frozen on CSV import persist)
  projectId?: number; // Project that owns this request – used for per-project limit checks
  refreshTrigger?: number; // Optional trigger to force reload of data sets
  onDataChange?: (dataArr: any[]) => void; // Callback when dataArr changes (for auto-save)
  /** Persist table rows immediately (e.g. after CSV import). Body tab unmount cancels debounced auto-save. */
  onFlushTablePersist?: (ctx: {
    requestId: number;
    method?: string;
    dataArr: any[];
  }) => void | Promise<void | any[]>;
  onDuplicateRow?: (newDataArr: any[], duplicateInsertedAt: number) => void; // Callback when a row is duplicated so parent can persist immediately (avoids 2s auto-save creating a second row)
  // Milestone 2: prefer emitting duplicate intent by ID (Model A option 1).
  // rowIndex: where to insert the duplicate (below current row = rowIndex + 1)
  onDuplicateDataSetId?: (dataSetId: number, rowIndex: number) => void;
  /** When requestId is set (Data Sets mode), parent creates the new row (e.g. draft with body from request); table columns come from that body. */
  onAddDataSet?: () => void;
  /** Called after bulk or AI generation succeeds so parent can refresh data sets. */
  onDataSetsGenerated?: () => Promise<void>;
  allDataSets?: RequestDataSet[]; // All data sets from parent (includes newly created ones)
  /** Execution results for Last Executed / Status columns. Key: dataSetId. */
  executionResultsByDataSetId?: Record<number, { lastExecuted: string; status: string }>;
  // Bulk execution state props
  isExecutingAllDataSets?: boolean; // Whether bulk execution is in progress
  currentlyExecutingDataSetId?: number | null; // ID of the data set currently being executed
  executionProgress?: { current: number; total: number }; // Progress of bulk execution
  /** Optional ref to receive latest editor value on every keystroke (before debounce). Used to flush pending edits before save. */
  latestEditorValueRef?: React.MutableRefObject<string | null>;
  /** When provided, Search bar is rendered in this header slot instead of inside the editor toolbar */
  searchContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Raw body content type (JSON, Text, JavaScript, HTML, XML) - affects syntax highlighting and validation. Postman-style. */
  rawBodySubtype?: string;
  /** When provided, Beautify button is portaled into this header slot (Postman-style, near body type controls). */
  beautifySlotRef?: React.RefObject<HTMLDivElement | null>;
}

const RawJsonTable: React.FC<RawJsonTableProps> = ({
  value = '',
  onChange,
  onFocus,
  onBlur,
  onSave,
  onDelete,
  onDeleteMultiple,
  onExecute,
  onExecuteDataSetId,
  onDataSetSelect,
  selectedDataSetId,
  requestId,
  requestMethod,
  projectId,
  refreshTrigger,
  onDataChange,
  onFlushTablePersist,
  onDuplicateRow,
  onDuplicateDataSetId,
  onAddDataSet,
  onDataSetsGenerated,
  allDataSets = [],
  executionResultsByDataSetId = {},
  isExecutingAllDataSets = false,
  currentlyExecutingDataSetId = null,
  executionProgress = { current: 0, total: 0 },
  latestEditorValueRef,
  searchContainerRef,
  rawBodySubtype = 'JSON',
  beautifySlotRef,
}) => {
  const dataArrRef = useRef<any[]>([]);
  const [verticalViewOpen, setVerticalViewOpen] = useState(false);
  const [verticalViewRow, setVerticalViewRow] = useState<any>(null);
  const [verticalViewRowIndex, setVerticalViewRowIndex] = useState<number | null>(null);
  const [dataArr, setDataArr] = useState<any[]>([]);
  dataArrRef.current = dataArr;
  const [error, setError] = useState('');
  const [selectedRowIdx, setSelectedRowIdx] = useState<number>(0);
  const [editorValue, setEditorValue] = useState(value);
  const [beautifyPortalTarget, setBeautifyPortalTarget] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [savedDataSets, setSavedDataSets] = useState<RequestDataSet[]>([]);
  const [isSavingRow, setIsSavingRow] = useState(false);
  // Milestone 2: this component no longer fetches execution results itself.
  const [executionResults] = useState<Map<number, { lastExecuted?: string; status?: string; count?: number }>>(new Map());
  const isUpdatingFromCellEdit = React.useRef(false); // Track if we're updating from cell edit
  const isCellEditingRef = React.useRef(false); // True while a table cell input is focused
  const isUserEditingUnsavedRow = React.useRef(false); // Track if user is actively editing an unsaved row
  const isUserTypingInEditorRef = React.useRef(false); // Prevent value sync from overwriting in-progress typing (cursor jump)
  const isEditorFocusedRef = React.useRef(false); // Keep editor stable while focused (prevents remount/flicker while typing)
  const valueSetByEditorRef = React.useRef(false); // Value was set by editor onChange - don't remount (prevents cursor jump)
  const isSyncingEditorFromTableRef = React.useRef(false); // Imperative table→editor sync — skip onChange echo
  const monacoEditorRef = React.useRef<any>(null);
  const [requestBodyEditorKey, setRequestBodyEditorKey] = React.useState(0); // Remount editor on external value changes only
  const prevValueRef = React.useRef(value);
  const prevAllDataSetsRef = React.useRef<RequestDataSet[]>([]); // Track previous allDataSets to detect changes
  // Milestone 2: bulk/AI generation should be handled by parent (pure table).
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    rowIndex: number | null;
    dataSetName: string;
    dataSetId?: number;
  }>({
    open: false,
    rowIndex: null,
    dataSetName: '',
    dataSetId: undefined
  });
  const [selectedForDelete, setSelectedForDelete] = useState<Set<number>>(new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCount, setBulkCount] = useState<string>('10');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ created: number; requested: number } | null>(null);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPositiveCount, setAiPositiveCount] = useState<string>('3');
  const [aiNegativeCount, setAiNegativeCount] = useState<string>('2');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState<{ created: number; requested: number; message?: string } | null>(null);
  const [successSnackbar, setSuccessSnackbar] = useState<string | null>(null);
  const [requestBodyCopied, setRequestBodyCopied] = useState(false);
  const [tableScrollTop, setTableScrollTop] = useState(0);
  const [tableViewportHeight, setTableViewportHeight] = useState(480);
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleCopyRequestBody = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      setRequestBodyCopied(true);
      setTimeout(() => setRequestBodyCopied(false), 1500);
    } catch {
      setRequestBodyCopied(false);
    }
  };

  const extractApiError = (err: any): string => {
    const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message;
    return typeof msg === 'string' ? msg : 'Operation failed';
  };
  const tableContainerInnerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  /** Original typeof for in-progress cell edits (inputs always emit strings). */
  const cellOriginalTypeRef = useRef<Map<string, string>>(new Map());
  const requestBodyLeafTypesRef = useRef<Map<string, string>>(new Map());
  /** Persists strongest leaf types seen for this request (empty templates must not downgrade userId:number → string). */
  const stableRequestBodyLeafTypesRef = useRef<Map<string, string>>(new Map());
  const cellEditKey = (rowIdx: number, key: string) => `${rowIdx}\0${key}`;

  // Force table container to respect parent width - debounced to prevent flickering
  useEffect(() => {
    const updateTableWidth = () => {
      if (tableContainerRef.current) {
        const parentWidth = tableContainerRef.current.offsetWidth;
        if (parentWidth > 0) {
          if (tableContainerInnerRef.current) {
            (tableContainerInnerRef.current as any).style.maxWidth = `${parentWidth}px`;
            (tableContainerInnerRef.current as any).style.width = `${parentWidth}px`;
          }
        }
      }
    };

    // Immediate update on mount/requestId change to prevent initial flicker
    updateTableWidth();
    
    // Debounce subsequent updates to prevent rapid updates during data changes
    const timeoutId = setTimeout(updateTableWidth, 50);
    window.addEventListener('resize', updateTableWidth);
    
    // Also update when data changes, but with a slight delay to let DOM settle
    const dataTimeoutId = setTimeout(updateTableWidth, 150);
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(dataTimeoutId);
      window.removeEventListener('resize', updateTableWidth);
    };
  }, [dataArr.length, requestId]);

  // Template row keys: prefer ref set by column computation (request body or data-driven columns), else first row keys
  const templateColumnKeysRef = useRef<string[]>([]);
  const getTemplateObject = () => {
    const keys = (
      templateColumnKeysRef.current.length
        ? templateColumnKeysRef.current
        : Object.keys(dataArr[0] || {}).filter(
            (k) => !TABLE_ROW_META_KEYS.has(k) && k !== '__rawBody' && !k.includes('.')
          )
    );
    if (keys.length === 0) return {};
    const flat: Record<string, unknown> = {};
    const types = requestBodyLeafTypesRef.current;
    for (const k of keys) {
      const t = types.get(k);
      if (t === 'array') flat[k] = [];
      else if (t === 'boolean') flat[k] = '';
      else if (t === 'null') flat[k] = null;
      else flat[k] = '';
    }
    return unflattenFlatKeys(flat);
  };

  const displayDataArr = dataArr;
  const paginatedDataArr = useMemo(
    () =>
      displayDataArr.slice(
        tablePage * tableRowsPerPage,
        tablePage * tableRowsPerPage + tableRowsPerPage
      ),
    [displayDataArr, tablePage, tableRowsPerPage]
  );
  const useVirtualTableRows = paginatedDataArr.length >= LARGE_DATA_SET_TABLE_THRESHOLD;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(displayDataArr.length / tableRowsPerPage) - 1);
    if (tablePage > maxPage) setTablePage(maxPage);
  }, [displayDataArr.length, tableRowsPerPage, tablePage]);

  useEffect(() => {
    if (selectedDataSetId == null) return;
    const idx = displayDataArr.findIndex((r) => r?._savedDataSetId === selectedDataSetId);
    if (idx < 0) return;
    const pageForRow = Math.floor(idx / tableRowsPerPage);
    setTablePage((prev) => (prev === pageForRow ? prev : pageForRow));
  }, [selectedDataSetId, displayDataArr, tableRowsPerPage]);

  useEffect(() => {
    setTablePage(0);
  }, [requestId]);

  useEffect(() => {
    stableRequestBodyLeafTypesRef.current = new Map();
  }, [requestId]);

  const savedDataSetsById = useMemo(() => {
    const map = new Map<number, RequestDataSet>();
    for (const ds of savedDataSets) {
      if (ds?.id != null) map.set(ds.id, ds);
    }
    return map;
  }, [savedDataSets]);

  const allDataSetsById = useMemo(() => {
    const map = new Map<number, RequestDataSet>();
    for (const ds of allDataSets) {
      if (ds?.id != null) map.set(ds.id, ds);
    }
    return map;
  }, [allDataSets]);

  const virtualRowWindow = useMemo(() => {
    if (!useVirtualTableRows) {
      return { start: 0, end: paginatedDataArr.length, topSpacer: 0, bottomSpacer: 0 };
    }
    const start = Math.max(
      0,
      Math.floor(tableScrollTop / VIRTUAL_ROW_HEIGHT_PX) - VIRTUAL_OVERSCAN_ROWS
    );
    const visibleCount = Math.ceil(tableViewportHeight / VIRTUAL_ROW_HEIGHT_PX) + VIRTUAL_OVERSCAN_ROWS * 2;
    const end = Math.min(paginatedDataArr.length, start + visibleCount);
    return {
      start,
      end,
      topSpacer: start * VIRTUAL_ROW_HEIGHT_PX,
      bottomSpacer: Math.max(0, (paginatedDataArr.length - end) * VIRTUAL_ROW_HEIGHT_PX),
    };
  }, [useVirtualTableRows, paginatedDataArr.length, tableScrollTop, tableViewportHeight]);

  const visibleTableRows = useMemo(
    () => paginatedDataArr.slice(virtualRowWindow.start, virtualRowWindow.end),
    [paginatedDataArr, virtualRowWindow.start, virtualRowWindow.end]
  );

  // Only update editorValue from table when selectedRowIdx changes (not on every data change)
  const prevSelectedRowIdx = useRef<number>(selectedRowIdx);
  useEffect(() => {
    if (prevSelectedRowIdx.current !== selectedRowIdx) {
      const row = dataArr[selectedRowIdx] || {};
      const textTemplate =
        typeof row.__textTemplate === 'string'
          ? row.__textTemplate
          : rawBodySubtype !== 'JSON'
            ? unwrapLazyBodyWrapper((value ?? editorValue ?? '').trim())
            : '';
      if (textTemplate && extractVariablePlaceholderKeys(textTemplate).length > 0) {
        const fieldMap = extractFlatFieldMapFromRow(row);
        const hasValues = Object.keys(fieldMap).some((k) => !TABLE_ROW_META_KEYS.has(k));
        setEditorValue(
          hasValues
            ? applyRowValuesToTextTemplate(textTemplate, fieldMap)
            : textTemplate
        );
        setError('');
        prevSelectedRowIdx.current = selectedRowIdx;
        return;
      }
      if (typeof row.__rawBody === 'string') {
        setEditorValue(unwrapLazyBodyWrapper(row.__rawBody));
        setError('');
        prevSelectedRowIdx.current = selectedRowIdx;
        return;
      }
      if (typeof row.__lazyBody === 'string') {
        setEditorValue(unwrapLazyBodyWrapper(row.__lazyBody));
        setError('');
        prevSelectedRowIdx.current = selectedRowIdx;
        return;
      }
      const flat = getFlatRowForDisplay(row);
      const cleanedJson = getRequestBodyFromTableRow(row) || '';
      setEditorValue(cleanedJson);
      setError('');
      prevSelectedRowIdx.current = selectedRowIdx;
    }
  }, [selectedRowIdx, dataArr, requestId, rawBodySubtype]);

  const valueChangeFromEditor = (nextValue: string | undefined) =>
    valueSetByEditorRef.current ||
    (latestEditorValueRef?.current != null && nextValue === latestEditorValueRef.current);

  const syncMonacoContentFromTable = useCallback((text: string) => {
    isSyncingEditorFromTableRef.current = true;
    const editor = monacoEditorRef.current;
    if (editor) {
      const model = editor.getModel();
      if (model && model.getValue() !== text) {
        editor.pushUndoStop();
        editor.executeEdits('table-sync', [
          {
            range: model.getFullModelRange(),
            text,
            forceMoveMarkers: true,
          },
        ]);
        editor.pushUndoStop();
      }
    }
    setEditorValue(text);
    if (latestEditorValueRef) latestEditorValueRef.current = text;
    // Keep the guard long enough for Monaco's async onChange echo after executeEdits.
    window.setTimeout(() => {
      isSyncingEditorFromTableRef.current = false;
    }, 100);
  }, [latestEditorValueRef]);

  // Remount Request Body editor when parent value changes externally (data set switch) — not editor/cell typing.
  React.useEffect(() => {
    if (value === prevValueRef.current) return;

    // Never remount or overwrite while the user is actively editing — prevents flicker and lost input.
    if (isUserTypingInEditorRef.current || isEditorFocusedRef.current) {
      if (valueChangeFromEditor(value)) {
        valueSetByEditorRef.current = false;
        prevValueRef.current = value;
      }
      return;
    }

    if (valueChangeFromEditor(value)) {
      valueSetByEditorRef.current = false;
      prevValueRef.current = value;
      return;
    }

    prevValueRef.current = value;
    setEditorValue(value ?? '');
    setRequestBodyEditorKey((k) => k + 1);
  }, [value, latestEditorValueRef]);

  // Keep latestEditorValueRef in sync with value prop (e.g. when switching data sets)
  useEffect(() => {
    if (!latestEditorValueRef) return;
    if (isUserTypingInEditorRef.current || isEditorFocusedRef.current) return;
    latestEditorValueRef.current = value;
  }, [value, latestEditorValueRef]);

  // Update editorValue when value prop changes and parse it to update dataArr
  // BUT: Skip if we're updating from a cell edit to prevent clearing existing rows
  useEffect(() => {
    // Skip if we're in the middle of a cell edit
    if (isUpdatingFromCellEdit.current || isCellEditingRef.current) {
      return;
    }

    // IMPORTANT: When this component is used in POST/PUT "Data Sets" mode (requestId is present),
    // the table rows come from saved data sets + user edits, NOT from the Request Body editor.
    // While typing JSON in the Request Body, the value can temporarily become valid JSON and
    // this effect used to rebuild dataArr (e.g. when value is an array), which caused duplicate
    // rows to appear in the data table. So in requestId mode we only mirror the editor text.
    if (requestId) {
      // Don't overwrite editorValue when user is actively typing (prevents cursor jumping to end)
      if (isUserTypingInEditorRef.current || isEditorFocusedRef.current) return;
      if (valueChangeFromEditor(value)) {
        valueSetByEditorRef.current = false;
        return;
      }
      if (value !== editorValue) {
        setEditorValue(value);
      }
      return;
    }
    
    // Handle initial load: if dataArr is empty and value is not empty, initialize dataArr
    // This handles the case when component mounts with a non-empty value (e.g., from cURL import)
    if (dataArr.length === 0 && value && value.trim()) {
      try {
        const obj = JSON.parse(value);
        if (Array.isArray(obj)) {
          const existingKeys = obj.length > 0 ? Object.keys(obj[0]) : [];
          const getTemplateObject = () => Object.fromEntries(existingKeys.map(k => [k, '']));
          setDataArr(obj.map(o => ({ ...getTemplateObject(), ...o })));
          setSelectedRowIdx(0);
          setError('');
          setEditorValue(value);
          return;
        } else if (typeof obj === 'object' && obj !== null) {
          const existingKeys = Object.keys(obj);
          const getTemplateObject = () => Object.fromEntries(existingKeys.map(k => [k, '']));
          const filled = { ...getTemplateObject(), ...obj };
          setDataArr([filled]);
          setSelectedRowIdx(0);
          setError('');
          setEditorValue(value);
          return;
        }
      } catch (e) {
        // If parsing fails, still update editorValue if it's different
        if (value !== editorValue) {
          setEditorValue(value);
        }
        return;
      }
    }
    
    // Handle initial load: if dataArr is empty and value is not empty, initialize dataArr
    // This handles the case when component mounts with a non-empty value (e.g., from cURL import)
    if (dataArr.length === 0 && value && value.trim()) {
      try {
        const obj = JSON.parse(value);
        if (Array.isArray(obj)) {
          const existingKeys = obj.length > 0 ? Object.keys(obj[0]) : [];
          const getTemplateObject = () => Object.fromEntries(existingKeys.map(k => [k, '']));
          setDataArr(obj.map(o => ({ ...getTemplateObject(), ...o })));
          setSelectedRowIdx(0);
          setError('');
          setEditorValue(value);
          return;
        } else if (typeof obj === 'object' && obj !== null) {
          const existingKeys = Object.keys(obj);
          const getTemplateObject = () => Object.fromEntries(existingKeys.map(k => [k, '']));
          const filled = { ...getTemplateObject(), ...obj };
          setDataArr([filled]);
          setSelectedRowIdx(0);
          setError('');
          setEditorValue(value);
          return;
        }
      } catch (e) {
        // If parsing fails, still update editorValue if it's different
        if (value !== editorValue) {
          setEditorValue(value);
        }
        return;
      }
    }
    
    if (value !== editorValue) {
      setEditorValue(value);
      
      // Only update dataArr if it's empty (initial load) or if value is an array
      // Never reset dataArr when we already have multiple rows and value is a single object
      if (value && value.trim()) {
        try {
          const obj = JSON.parse(value);
          
          // Only update dataArr if:
          // 1. dataArr is empty (initial load), OR
          // 2. value is an array (represents multiple rows)
          // Never update if we have multiple rows and value is a single object
          if (dataArr.length === 0) {
            // Initial load - always update
            if (Array.isArray(obj)) {
              const existingKeys = obj.length > 0 ? Object.keys(obj[0]) : [];
              const getTemplateObject = () => Object.fromEntries(existingKeys.map(k => [k, '']));
              setDataArr(obj.map(o => ({ ...getTemplateObject(), ...o })));
              setSelectedRowIdx(0);
              setError('');
            } else if (typeof obj === 'object' && obj !== null) {
              const existingKeys = Object.keys(obj);
              const getTemplateObject = () => Object.fromEntries(existingKeys.map(k => [k, '']));
              const filled = { ...getTemplateObject(), ...obj };
              setDataArr([filled]);
              setSelectedRowIdx(0);
              setError('');
            }
          } else if (Array.isArray(obj) && obj.length !== dataArr.length) {
            // Value is an array with different length - update (user loaded different data)
            const existingKeys = dataArr.length > 0 ? Object.keys(dataArr[0]) : (obj.length > 0 ? Object.keys(obj[0]) : []);
            const getTemplateObject = () => Object.fromEntries(existingKeys.map(k => [k, '']));
            setDataArr(obj.map(o => ({ ...getTemplateObject(), ...o })));
            setSelectedRowIdx(0);
            setError('');
          }
          // If we have existing rows and value is a single object, DON'T reset dataArr
          // This preserves existing rows when user edits cells
        } catch (e) {
          // Don't set error on parse failure when value prop changes
        }
      }
    }
  }, [value]);

  // Milestone 2: pure table (no API calls).
  // Source data sets from `allDataSets` provided by parent and parse once per change.
  useEffect(() => {
    if (!requestId) {
      setSavedDataSets([]);
      setDataArr([]);
      setLoading(false);
      return;
    }

    // Skip while user is typing in Request Body (parent rawBody / allDataSets can update on each keystroke)
    if (isUserTypingInEditorRef.current || isEditorFocusedRef.current) {
      return;
    }

    // Skip overwriting dataArr when user is actively editing a cell (prevents losing draft edits)
    if (isUpdatingFromCellEdit.current || isCellEditingRef.current) {
      return;
    }

    setLoading(false);
    const datasets = allDataSets || [];
    setSavedDataSets(datasets);
    prevAllDataSetsRef.current = [...datasets];

    const textTemplateForRows =
      rawBodySubtype !== 'JSON'
        ? resolveRequestBodyTextForColumns(
            value,
            editorValue,
            latestEditorValueRef?.current,
            allDataSets,
            rawBodySubtype
          )
        : '';

    const applyParsedRows = (parsedRows: any[]) => {
      setDataArr((prev) => {
        const persistedIdsInParsed = new Set(
          parsedRows
            .map((r) => r?._savedDataSetId)
            .filter((id): id is number => id != null && Number(id) > 0)
        );
        const deletedPersistedIds = new Set<number>();
        for (const row of prev) {
          const id = row?._savedDataSetId;
          if (id != null && Number(id) > 0 && !persistedIdsInParsed.has(Number(id))) {
            deletedPersistedIds.add(Number(id));
          }
        }
        const deletedBodySignatures = new Set(
          prev
            .filter((r) => {
              const id = r?._savedDataSetId;
              return id != null && deletedPersistedIds.has(Number(id));
            })
            .map(tableRowPersistedBodySignature)
            .filter((s) => s !== '')
        );
        const unsavedTrailing = prev.filter(
          (r) =>
            r != null &&
            r._savedDataSetId == null &&
            r._savedDataSetIndex == null &&
            tableRowHasContent(r)
        );
        const parsedSignatures = new Set(
          parsedRows.map(tableRowPersistedBodySignature).filter((s) => s !== '')
        );
        const freshUnsavedTrailing = unsavedTrailing.filter((r) => {
          const sig = tableRowPersistedBodySignature(r);
          if (sig !== '' && parsedSignatures.has(sig)) return false;
          if (sig !== '' && deletedBodySignatures.has(sig)) return false;
          return true;
        });
        // The backend creates one empty Data Set 1 on POST/PUT/PATCH create. Typing a body
        // before that row is linked must fill Data Set 1, not append a second row.
        const onlyDefaultPersisted =
          datasets.length === 1 &&
          parsedRows.length === 1 &&
          freshUnsavedTrailing.length === 1;
        const defaultBody = String(datasets[0]?.body ?? '').trim();
        const defaultBodyIsPlaceholder = defaultBody === '' || defaultBody === '{}';
        const next = onlyDefaultPersisted && defaultBodyIsPlaceholder
          ? [
              {
                ...freshUnsavedTrailing[0],
                _savedDataSetId: parsedRows[0]?._savedDataSetId,
                _savedDataSetIndex: parsedRows[0]?._savedDataSetIndex ?? 0,
              },
            ]
          : freshUnsavedTrailing.length === 0
            ? parsedRows
            : [...parsedRows, ...freshUnsavedTrailing];
        if (tableRowsSemanticallyEqual(prev, next)) return prev;
        if (!isUpdatingFromCellEdit.current && onDataChange) {
          queueMicrotask(() => onDataChange(next));
        }
        return next;
      });
      const nextIdx = selectedDataSetId
        ? parsedRows.findIndex((r) => r?._savedDataSetId === selectedDataSetId)
        : 0;
      if (nextIdx >= 0) {
        setSelectedRowIdx((prev) => (prev === nextIdx ? prev : nextIdx));
      }
    };

    // Large lists: lazy row bodies + chunked build so bulk import does not freeze the tab.
    if (datasets.length > LARGE_DATA_SET_TABLE_THRESHOLD) {
      let cancelled = false;
      setLoading(true);
      const lazyParse = true;
      const runChunked = async () => {
        const parsedRows: any[] = [];
        for (let i = 0; i < datasets.length; i += PARSE_DATA_SETS_CHUNK_SIZE) {
          if (cancelled) return;
          const end = Math.min(i + PARSE_DATA_SETS_CHUNK_SIZE, datasets.length);
          for (let j = i; j < end; j++) {
            parsedRows.push(
              parseDataSetToTableRow(datasets[j], j, lazyParse, textTemplateForRows || undefined)
            );
          }
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
        }
        if (!cancelled) {
          applyParsedRows(parsedRows);
          setLoading(false);
        }
      };
      void runChunked();
      return () => {
        cancelled = true;
      };
    }

    applyParsedRows(
      datasets.map((ds, index) =>
        parseDataSetToTableRow(ds, index, false, textTemplateForRows || undefined)
      )
    );
  }, [requestId, allDataSets, selectedDataSetId, refreshTrigger, rawBodySubtype, value, editorValue, latestEditorValueRef]);

  // When saved data sets have empty bodies, seed table rows from the request body template so columns/values appear.
  useEffect(() => {
    if (!requestId) return;
    if (isUserTypingInEditorRef.current || isEditorFocusedRef.current) return;
    if (isUpdatingFromCellEdit.current || isCellEditingRef.current) return;

    const bodyText = resolveRequestBodyTextForColumns(
      value,
      latestEditorValueRef?.current ?? value,
      latestEditorValueRef?.current,
      allDataSets,
      rawBodySubtype
    );
    const templateKeys = getRequestBodyColumnKeys(bodyText, rawBodySubtype);
    if (templateKeys.length === 0) return;

    setDataArr((prev) => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((row) => {
        if (!row || typeof row !== 'object') return row;
        const flat = getFlatRowForDisplay(row);
        const hasFieldKeys = Object.keys(flat).some(
          (k) => !TABLE_ROW_META_KEYS.has(k) && k !== '__rawBody'
        );
        if (hasFieldKeys) return row;
        changed = true;
        const leafTypes = mergeRequestBodyLeafTypes(
          collectRequestBodyTextCandidates(
            value,
            latestEditorValueRef?.current ?? value,
            latestEditorValueRef?.current,
            allDataSets
          )
        );
        const fieldTemplate = Object.fromEntries(
          templateKeys.map((k) => {
            const t = leafTypes.get(k);
            if (t === 'array') return [k, flat[k] ?? []];
            if (t === 'boolean') return [k, flat[k] ?? ''];
            if (t === 'null') return [k, flat[k] ?? null];
            return [k, flat[k] ?? ''];
          })
        );
        const isTextWithVars = extractVariablePlaceholderKeys(bodyText).length > 0;
        if (isTextWithVars) {
          return {
            ...fieldTemplate,
            __textTemplate: bodyText,
            _savedDataSetId: row._savedDataSetId,
            _savedDataSetIndex: row._savedDataSetIndex,
          };
        }
        const template = unflattenFlatKeys(fieldTemplate);
        return {
          ...template,
          _savedDataSetId: row._savedDataSetId,
          _savedDataSetIndex: row._savedDataSetIndex,
        };
      });
      if (!changed) return prev;
      if (!isUpdatingFromCellEdit.current && onDataChange) {
        queueMicrotask(() => onDataChange(next));
      }
      return next;
    });
  }, [requestId, value, allDataSets, refreshTrigger, onDataChange, latestEditorValueRef, rawBodySubtype, editorValue]);

  // Milestone 2: pure table.
  // - `savedDataSets` is derived from `allDataSets` in the effect above.
  // - This component does not fetch execution results or sync from API.

  // When editorValue changes, update the selected row in dataArr
  // This is called when user directly edits the JSON editor (not from cell edits)
  // Debounce timer for parent onChange and table update
  const editorChangeTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleEditorChange = (val: string | undefined) => {
    if (
      isSyncingEditorFromTableRef.current ||
      isUpdatingFromCellEdit.current ||
      isCellEditingRef.current
    ) {
      return;
    }
    const value = unwrapLazyBodyWrapper(val ?? '');
    setError('');
    // Keep local editor state in sync for defaultValue remounts and beautify.
    setEditorValue(value);
    if (latestEditorValueRef) latestEditorValueRef.current = value;
    isUserTypingInEditorRef.current = true;
    // Sync parent immediately; valueSetByEditorRef prevents remount / prop sync from fighting the cursor.
    if (onChange) {
      valueSetByEditorRef.current = true;
      onChange(value);
    }

    if (editorChangeTimeout.current) {
      clearTimeout(editorChangeTimeout.current);
    }
    editorChangeTimeout.current = setTimeout(() => {
      if (!isEditorFocusedRef.current) {
        isUserTypingInEditorRef.current = false;
      }
      if (!value.trim()) {
        setError('');
        return;
      }
      // Postman-style: for non-JSON types (Text, JavaScript, HTML, XML), skip JSON validation
      if (rawBodySubtype !== 'JSON') {
        setError('');
        return;
      }
      try {
        const obj = JSON.parse(value);
        setError('');
        const cleanedObj = cleanDataForDisplay(obj);

        // When requestId is set (POST/PUT Data Sets mode), rows come ONLY from allDataSets + draft.
        // Only update existing row in place when user edits the body. When no rows yet, add first row from body.
        if (requestId != null) {
          setDataArr(prev => {
            const idx = selectedRowIdx;
            const existingPersistedId = (allDataSets ?? []).find(
              (ds) => ds?.id != null && Number(ds.id) > 0
            )?.id;
            const preserveMeta = (existingRow: any) => ({
              _savedDataSetId: existingRow?._savedDataSetId ?? existingPersistedId,
              _savedDataSetIndex:
                existingRow?._savedDataSetIndex ?? (existingPersistedId != null ? 0 : undefined),
            });
            // While the editor is focused, keep the exact body text on the row. Parsing into
            // discrete fields can re-serialize differently and fight the live editor (e.g. after `:""`).
            if (isEditorFocusedRef.current || isUserTypingInEditorRef.current) {
              const parsedFields =
                cleanedObj &&
                typeof cleanedObj === 'object' &&
                !Array.isArray(cleanedObj)
                  ? flatten(cleanedObj)
                  : null;
              const rowFromBody = (existingRow: any) => {
                const meta = preserveMeta(existingRow);
                if (!parsedFields) {
                  return { ...(existingRow ?? {}), __rawBody: value, ...meta };
                }
                return {
                  ...parsedFields,
                  __rawBody: value,
                  ...meta,
                };
              };
              if (prev.length === 0) {
                const arr = [rowFromBody(undefined)];
                if (onDataChange) onDataChange(arr);
                return arr;
              }
              if (idx < 0 || idx >= prev.length) return prev;
              const arr = [...prev];
              arr[idx] = rowFromBody(arr[idx]);
              if (onDataChange) onDataChange(arr);
              return arr;
            }
            if (Array.isArray(cleanedObj)) {
              const rowFromArray = { __rawBody: value, ...preserveMeta(prev[idx]) };
              if (prev.length === 0) {
                const arr = [rowFromArray];
                if (onDataChange) onDataChange(arr);
                return arr;
              }
              if (idx < 0 || idx >= prev.length) return prev;
              const arr = [...prev];
              arr[idx] = rowFromArray;
              if (onDataChange) onDataChange(arr);
              return arr;
            }
            const filled = rowToNestedStorage(
              typeof cleanedObj === 'object' && cleanedObj !== null
                ? { ...getTemplateObject(), ...cleanedObj }
                : prev[idx] || getTemplateObject()
            );
            if (prev.length === 0) {
              const arr = [{ ...filled, ...preserveMeta(undefined) }];
              if (onDataChange) onDataChange(arr);
              return arr;
            }
            if (idx < 0 || idx >= prev.length) return prev;
            const existingRow = prev[idx];
            const arr = [...prev];
            arr[idx] = {
              ...filled,
              _savedDataSetId: existingRow._savedDataSetId,
              _savedDataSetIndex: existingRow._savedDataSetIndex,
            };
            if (onDataChange) onDataChange(arr);
            return arr;
          });
          return;
        }

        // Non–Data Sets mode: allow creating/updating rows from editor (e.g. cURL import).
        if (Array.isArray(cleanedObj)) {
          setDataArr(cleanedObj.map(o => ({ ...getTemplateObject(), ...o })));
          setSelectedRowIdx(0);
        } else if (typeof cleanedObj === 'object' && cleanedObj !== null) {
          const filled = { ...getTemplateObject(), ...cleanedObj };
          setDataArr(prev => {
            const arr = [...prev];
            if (arr.length === 0) {
              return [{ ...filled, _savedDataSetId: undefined, _savedDataSetIndex: undefined }];
            }
            if (selectedRowIdx < arr.length) {
              const existingRow = arr[selectedRowIdx];
              arr[selectedRowIdx] = {
                ...filled,
                _savedDataSetId: existingRow._savedDataSetId,
                _savedDataSetIndex: existingRow._savedDataSetIndex
              };
            }
            return arr;
          });
        } else {
          throw new Error('Not a JSON object or array');
        }
      } catch (e) {
        let validWithFunctions = false;
        try {
          JSON.parse(stripFunctionCallsForJsonValidation(value));
          validWithFunctions = true;
          setError('');
        } catch {
          setError('Invalid JSON');
        }
        // Incomplete JSON while typing: keep __rawBody on the active row without parsing.
        if (requestId != null) {
          setDataArr((prev) => {
            if (prev.length === 0) return prev;
            const idx = selectedRowIdx;
            if (idx < 0 || idx >= prev.length) return prev;
            const arr = [...prev];
            arr[idx] = { ...arr[idx], __rawBody: value };
            if (onDataChange) onDataChange(arr);
            return arr;
          });
        } else if (validWithFunctions) {
          // Non–data-set mode: body contains $functions; keep editor content without field split.
          setError('');
        }
      }
    }, 300);
  };

  const handleEditorFocus = () => {
    isEditorFocusedRef.current = true;
    isUserTypingInEditorRef.current = true;
    onFocus?.();
  };

  const handleEditorBlur = () => {
    if (editorChangeTimeout.current) {
      clearTimeout(editorChangeTimeout.current);
      editorChangeTimeout.current = null;
    }
    isEditorFocusedRef.current = false;
    isUserTypingInEditorRef.current = false;
    onBlur?.();
  };

  const handleBeautifyRequestBody = useCallback(() => {
    const content = (latestEditorValueRef?.current ?? editorValue ?? value ?? '').trim();
    if (!content) {
      setError('');
      return;
    }
    if (rawBodySubtype === 'Text') {
      setError('Beautify is not available for plain text.');
      return;
    }
    const result = validateAndFormatContent(content, rawBodySubtype);
    if (!result.isValid) {
      setError(result.error || `Invalid ${rawBodySubtype}`);
      return;
    }
    const formatted = result.formattedContent ?? content;
    setError('');
    if (editorChangeTimeout.current) {
      clearTimeout(editorChangeTimeout.current);
      editorChangeTimeout.current = null;
    }
    isEditorFocusedRef.current = false;
    isUserTypingInEditorRef.current = false;
    handleEditorChange(formatted);
    setRequestBodyEditorKey((k) => k + 1);
  }, [editorValue, value, rawBodySubtype, latestEditorValueRef]);

  // When format changes (e.g. JavaScript → JSON/HTML/XML), re-validate existing body content.
  // Live typing only validates inside handleEditorChange; subtype switch never typed, so it was skipped.
  useEffect(() => {
    // Avoid overriding live editor validation.
    if (isEditorFocusedRef.current || isUserTypingInEditorRef.current) return;

    const content = (latestEditorValueRef?.current ?? editorValue ?? value ?? '').trim();
    if (!content) {
      setError('');
      return;
    }

    if (rawBodySubtype === 'Text') {
      setError('');
      return;
    }

    if (
      rawBodySubtype !== 'JSON' &&
      rawBodySubtype !== 'HTML' &&
      rawBodySubtype !== 'XML' &&
      rawBodySubtype !== 'JavaScript'
    ) {
      setError('');
      return;
    }

    const result = validateAndFormatContent(content, rawBodySubtype);
    setError(result.isValid ? '' : (result.error || `Invalid ${rawBodySubtype}`));
  }, [rawBodySubtype, editorValue]);

  useLayoutEffect(() => {
    const el = beautifySlotRef?.current ?? null;
    setBeautifyPortalTarget((prev) => (prev === el ? prev : el));
  });

  const beautifyButton = (
    <Button
      variant="text"
      color="primary"
      size="small"
      onClick={handleBeautifyRequestBody}
      sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', px: 1 }}
    >
      Beautify
    </Button>
  );

  // When a cell is edited, update the selected row and editorValue.
  // Notify parent immediately so auto-save refs/state stay in sync (loop guards are elsewhere).
  const handleCellChange = (rowIdx: number, key: string, value: any) => {
    isUpdatingFromCellEdit.current = true;

    const result = buildCellEditResult(
      dataArrRef.current,
      rowIdx,
      key,
      value,
      cellOriginalTypeRef.current,
      requestBodyLeafTypesRef.current
    );
    if (!result) return;

    dataArrRef.current = result.arr;
    setDataArr(result.arr);

    if (result.selectUnsaved) {
      isUserEditingUnsavedRow.current = true;
      onDataSetSelect?.({ id: undefined } as any);
    } else if (result.updatedRow._savedDataSetId) {
      isUserEditingUnsavedRow.current = false;
    }

    setSelectedRowIdx((prev) => (prev === result.targetIdx ? prev : result.targetIdx));

    if (onDataChange) {
      onDataChange(result.arr);
    }

    window.setTimeout(() => {
      const jsonStr =
        typeof result.updatedRow.__rawBody === 'string'
          ? result.updatedRow.__rawBody
          : getRequestBodyFromTableRow(result.updatedRow);
      syncMonacoContentFromTable(jsonStr);
      window.setTimeout(() => {
        if (!isCellEditingRef.current) {
          isUpdatingFromCellEdit.current = false;
        }
      }, 100);
    }, 0);
  };

  const fetchDataSetPlanRemaining = async (): Promise<number | null> => {
    if (requestId == null) return null;
    try {
      const details = await apiService.getSubscriptionDetails();
      const lim = details?.limits;
      const perProjectMax = lim?.maxDatasets;
      if (perProjectMax == null || perProjectMax < 0) return null;

      const planUpper = (details.plan || '').toUpperCase();
      const projectScoped = planUpper === 'FREE' || planUpper === 'STANDARD' || planUpper === 'PROFESSIONAL';

      let remaining: number;

      if (projectScoped) {
        const maxProjects = lim?.maxProjects;
        const aggregateMax =
          maxProjects != null && maxProjects > 0 ? perProjectMax * maxProjects : perProjectMax;
        const tenantUsage = details?.usage?.datasets ?? 0;
        const aggregateRemaining = aggregateMax - tenantUsage;

        if (projectId != null) {
          try {
            const breakdown = await apiService.getSubscriptionProjectUsageBreakdown(projectId);
            const projectUsage = breakdown?.dataSets ?? 0;
            remaining = Math.min(perProjectMax - projectUsage, aggregateRemaining);
          } catch {
            remaining = aggregateRemaining;
          }
        } else {
          remaining = aggregateRemaining;
        }
      } else {
        const maxCap = lim?.maxDatasetsAllProjects ?? perProjectMax;
        remaining = maxCap - (details?.usage?.datasets ?? 0);
        if (lim?.maxDataSetsPerRequest != null && lim.maxDataSetsPerRequest >= 0) {
          try {
            const existing = await apiService.getDataSetsByRequest(requestId);
            remaining = Math.min(remaining, lim.maxDataSetsPerRequest - existing.length);
          } catch {
            // Fall back to tenant remaining only
          }
        }
      }

      return remaining > 0 ? remaining : 0;
    } catch {
      return null;
    }
  };

  const validateBulkCountAgainstPlan = async (count: number): Promise<string | null> => {
    const remaining = await fetchDataSetPlanRemaining();
    if (remaining == null) return null;
    if (remaining <= 0) {
      return 'Cannot generate data sets: your plan data set limit has been reached. Upgrade your plan or delete existing data sets.';
    }
    if (count > remaining) {
      return `Adding ${count} data set(s) would exceed your plan limit (${remaining} data set(s) remaining in this project).`;
    }
    return null;
  };

  // Add Data Set button: in Data Sets mode (requestId + onAddDataSet) parent creates the row with body from request so columns are dynamic; otherwise add a local row.
  const handleAddDataSet = () => {
    if (requestId != null && onAddDataSet) {
      onAddDataSet();
      return;
    }
    setDataArr(prev => {
      const next = [...prev, getTemplateObject()];
      const nextIdx = Math.max(0, next.length - 1);
      setSelectedRowIdx(nextIdx);
      if (requestId == null && onDataChange) onDataChange(next);
      return next;
    });
  };

  const handleBulkCountChange = (raw: string) => {
    setBulkCount(clampBulkCountInput(raw));
  };

  const parsedBulkCount = bulkCount === '' ? NaN : parseInt(bulkCount, 10);
  const bulkCountReady =
    bulkCount !== '' &&
    Number.isFinite(parsedBulkCount) &&
    parsedBulkCount >= MIN_BULK_GENERATE_COUNT &&
    parsedBulkCount <= MAX_BULK_GENERATE_COUNT;

  const handleBulkGenerate = async () => {
    const count = parseInt(bulkCount, 10);
    if (!requestId || !bulkCountReady || isNaN(count) || count < MIN_BULK_GENERATE_COUNT || count > MAX_BULK_GENERATE_COUNT) {
      setError(`Please enter a valid number between ${MIN_BULK_GENERATE_COUNT} and ${MAX_BULK_GENERATE_COUNT}`);
      return;
    }
    const planLimitError = await validateBulkCountAgainstPlan(count);
    if (planLimitError) {
      setError(planLimitError);
      return;
    }
    setBulkLoading(true);
    setBulkProgress({ created: 0, requested: count });
    setError('');
    try {
      const created = await apiService.generateBulkDataSetsWithFaker(requestId, count, setBulkProgress);
      const createdCount = Array.isArray(created) ? created.length : 0;
      setBulkDialogOpen(false);
      setBulkCount('10');
      setBulkProgress(null);
      // Let the dialog close and paint before parent reloads a large data-set list.
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      if (createdCount < count) {
        setError(`Plan limit exceeded: created ${createdCount} of ${count} data sets.`);
      }
      setSuccessSnackbar(`Created ${createdCount} data sets at the end of the table`);
      if (onDataSetsGenerated) {
        // Reload in the background so the success toast can paint before heavy table work.
        setTimeout(() => {
          void (async () => {
            setLoading(true);
            try {
              await onDataSetsGenerated();
            } finally {
              // loading cleared when allDataSets effect finishes
            }
          })();
        }, 0);
      }
    } catch (err: any) {
      setError(extractApiError(err) || 'Failed to generate bulk data sets');
      if (onDataSetsGenerated) {
        try {
          await onDataSetsGenerated();
        } catch {
          // Parent reload failed; table may still be stale until manual refresh
        }
      }
    } finally {
      setBulkProgress(null);
      setBulkLoading(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!requestId) {
      setError('Request ID is required for AI generation');
      return;
    }
    if (!aiPrompt?.trim()) {
      setError('Please enter a prompt describing your test data requirements');
      return;
    }
    const positive = aiPositiveCount === '' ? 0 : parseInt(aiPositiveCount, 10);
    const negative = aiNegativeCount === '' ? 0 : parseInt(aiNegativeCount, 10);
    if (!Number.isFinite(positive) || positive < 0 || positive > MAX_AI_TEST_CASE_COUNT) {
      setError('Positive count must be between 0 and 20');
      return;
    }
    if (!Number.isFinite(negative) || negative < 0 || negative > MAX_AI_TEST_CASE_COUNT) {
      setError('Negative count must be between 0 and 20');
      return;
    }
    if (positive === 0 && negative === 0) {
      setError('Please specify at least one positive or negative test case');
      return;
    }
    setAiLoading(true);
    setAiProgress(null);
    setError('');
    try {
      const requestedCount = positive + negative;
      const planLimitError = await validateBulkCountAgainstPlan(requestedCount);
      if (planLimitError) {
        setError(planLimitError);
        return;
      }
      setAiProgress({ created: 0, requested: requestedCount, message: 'Starting…' });
      const created = await apiService.generateDataSetsWithAI(
        requestId,
        aiPrompt.trim(),
        positive,
        negative,
        (progress) => setAiProgress(progress)
      );
      const createdCount = Array.isArray(created) ? created.length : 0;
      setAiDialogOpen(false);
      setAiPrompt('');
      setAiPositiveCount('3');
      setAiNegativeCount('2');
      await onDataSetsGenerated?.();
      if (createdCount < requestedCount) {
        const remaining = requestedCount - createdCount;
        setError(
          `Created ${createdCount} of ${requestedCount} AI data sets (${remaining} missing). ` +
            'The AI model may have hit output limits — run again to fill the gap, or generate in smaller batches (e.g. 10 positive + 10 negative per run).'
        );
        if (createdCount > 0) {
          setSuccessSnackbar(`Added ${createdCount} AI-generated data sets at the end of the table`);
        }
      } else {
        setSuccessSnackbar(`Created ${createdCount} AI-generated data sets at the end of the table`);
      }
    } catch (err: any) {
      setError(extractApiError(err) || 'Failed to generate data sets with AI');
    } finally {
      setAiProgress(null);
      setAiLoading(false);
    }
  };

  // CSV Export function
  const handleExportCSV = () => {
    if (dataArr.length === 0) {
      setError('No data to export');
      return;
    }

    try {
      // Get all columns from the current table
      const columns = allFlattenedKeysFiltered;
      
      if (columns.length === 0) {
        setError('No columns to export');
        return;
      }

      // Create CSV header row (escape column names if needed)
      const headerRow = columns.map(col => {
        const str = String(col);
        if (str.includes(',') || str.includes('\n') || str.includes('"')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',');
      
      // Create CSV data rows
      const dataRows = dataArr.map(row => {
        const flatRow = getFlatRowForDisplay(row);
        return columns.map(col => {
          const value = flatRow[col];
          // Handle different value types
          if (value === null || value === undefined) {
            return '';
          }
          if (typeof value === 'object') {
            // Stringify objects and arrays, escape quotes
            const str = JSON.stringify(value);
            return `"${str.replace(/"/g, '""')}"`;
          }
          // Escape quotes and wrap in quotes if contains comma, newline, or quote
          const str = String(value);
          if (str.includes(',') || str.includes('\n') || str.includes('"')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(',');
      });

      // Combine header and data rows
      const csvContent = [headerRow, ...dataRows].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `data-table-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setError('');
    } catch (err: any) {
      setError('Failed to export CSV: ' + (err.message || 'Unknown error'));
      console.error('Error exporting CSV:', err);
    }
  };

  // CSV Import function
  const handleImportCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Check file extension
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      void (async () => { try {
        const text = e.target?.result as string;
        if (!text) {
          setError('Failed to read CSV file');
          return;
        }

        // Parse CSV
        const lines = text.split('\n').filter(line => line.trim() !== '');
        if (lines.length === 0) {
          setError('CSV file is empty');
          return;
        }

        // Parse header row
        const headers = parseCSVLine(lines[0]);
        if (headers.length === 0) {
          setError('CSV file has no headers');
          return;
        }

        // Parse data rows
        const importedRows: any[] = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.length === 0) continue; // Skip empty rows
          
          // Create row object from CSV data
          const row: any = {};
          headers.forEach((header, index) => {
            const value = values[index] || '';
            // Try to parse as JSON if it looks like JSON, otherwise use as string
            if (value.trim().startsWith('{') || value.trim().startsWith('[')) {
              try {
                row[header] = JSON.parse(value);
              } catch {
                row[header] = value;
              }
            } else if (value === 'true' || value === 'false') {
              row[header] = value === 'true';
            } else if (value === 'null' || value === '') {
              row[header] = null;
            } else if (!isNaN(Number(value)) && value.trim() !== '') {
              row[header] = Number(value);
            } else {
              row[header] = value;
            }
          });
          
          // Use setValueAtPath to handle nested keys (e.g., "user.name")
          const nestedRow: any = {};
          Object.keys(row).forEach(key => {
            setValueAtPath(nestedRow, key, row[key]);
          });
          // Imported rows are new data sets — never inherit saved metadata from existing rows.
          delete nestedRow._savedDataSetId;
          delete nestedRow._savedDataSetIndex;
          importedRows.push(nestedRow);
        }

        if (importedRows.length === 0) {
          setError('No valid data rows found in CSV file');
          return;
        }

        // Enforce plan-based data set limits before adding imported rows
        let rowsToAdd = importedRows;
        if (requestId != null) {
          try {
            const remaining = await fetchDataSetPlanRemaining();
            if (remaining != null) {
              const existingUnsaved = dataArr
                .filter(tableRowHasContent)
                .filter((r) => r?._savedDataSetId == null).length;
              const importCap = Math.max(0, remaining - existingUnsaved);
              if (importCap <= 0) {
                setError('Cannot import: your plan data set limit has been reached. Upgrade your plan or delete existing data sets.');
                event.target.value = '';
                return;
              }
              if (importedRows.length > importCap) {
                rowsToAdd = importedRows.slice(0, importCap);
                setError(`Plan limit: imported ${rowsToAdd.length} of ${importedRows.length} rows (${importCap} data set(s) remaining for this project).`);
              }
            }
          } catch {
            // If subscription check fails, let the backend enforce on save
          }
        }

        // Append imported rows as NEW data sets (keep existing rows with content).
        // Imported rows carry no _savedDataSetId, so the parent flush persists them as new rows
        // instead of overwriting existing data sets in place.
        let mergedRows: any[] = [];
        flushSync(() => {
          setDataArr(prev => {
            const existingContentRows = prev.filter(tableRowHasContent);
            mergedRows = [...existingContentRows, ...rowsToAdd];
            // Always notify parent so ref is updated; otherwise Update button won't persist imported rows (requestId was blocking this)
            if (onDataChange) onDataChange(mergedRows);
            return mergedRows;
          });
        });

        // TabPanel unmounts this component when leaving Body tab, which clears the 2s auto-save timeout.
        // Flush to backend now so imported rows survive navigation (freeze request id/method from import time).
        let persistFailed = false;
        const importRequestId = requestId;
        const importMethod = requestMethod;
        if (importRequestId != null && rowsToAdd.length > 0 && onFlushTablePersist) {
          try {
            const persistedRows = await onFlushTablePersist({
              requestId: importRequestId,
              method: importMethod,
              dataArr: mergedRows,
            });
            if (Array.isArray(persistedRows) && persistedRows.length > 0) {
              flushSync(() => {
                setDataArr(persistedRows);
                if (onDataChange) onDataChange(persistedRows);
              });
            }
          } catch (persistErr: any) {
            persistFailed = true;
            console.error('Failed to persist CSV import:', persistErr);
            setError(extractApiError(persistErr) || 'Failed to save imported data. Try again or save from the table.');
          }
        }

        if (!persistFailed && rowsToAdd.length === importedRows.length) {
          setError('');
        }
        // Reset file input
        event.target.value = '';
      } catch (err: any) {
        setError('Failed to import CSV: ' + (err.message || 'Unknown error'));
        console.error('Error importing CSV:', err);
        event.target.value = '';
      }
      })();
    };

    reader.onerror = () => {
      setError('Failed to read CSV file');
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  // Helper function to parse CSV line (handles quoted values)
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    result.push(current);
    
    return result;
  };

  // Handle duplicate row action. Use rowData when provided so we duplicate the clicked row (avoids first-row duplicating last).
  const handleDuplicate = (rowIndex: number, rowData?: any) => {
    if (rowIndex >= dataArr.length) {
      setError('Cannot duplicate an empty row');
      return;
    }

    const row = rowData != null ? rowData : dataArr[rowIndex];
    if (!row) {
      setError('No data to duplicate');
      return;
    }

    // Milestone 2 / Model A option 1: emit intent only (parent inserts duplicate below current row).
    const rawSavedId = row?._savedDataSetId;
    const savedIdNum = rawSavedId != null && rawSavedId !== '' ? Number(rawSavedId) : NaN;
    if (Number.isFinite(savedIdNum) && savedIdNum > 0 && onDuplicateDataSetId) {
      onDuplicateDataSetId(savedIdNum, rowIndex);
      return;
    }

    // Create a copy of the row without the _savedDataSetId (so it's treated as a new/unsaved row)
    const duplicatedRow = cleanDataForDisplay(row);

    const duplicateInsertedAt = rowIndex + 1;
    const newDataArr = [...dataArr];
    newDataArr.splice(duplicateInsertedAt, 0, duplicatedRow);

    setDataArr(newDataArr);
    if (requestId == null && onDataChange) onDataChange(newDataArr);

    // Let parent persist the duplicate immediately so the 2-second auto-save does not create a second row
    if (onDuplicateRow) {
      onDuplicateRow(newDataArr, duplicateInsertedAt);
    }

    // Select the newly duplicated row
    setSelectedRowIdx(duplicateInsertedAt);
    setEditorValue(JSON.stringify(duplicatedRow, null, 2));
  };

  // Handle delete action - show confirmation dialog
  const handleDeleteClick = (rowIndex: number) => {
    if (rowIndex >= dataArr.length) {
      return;
    }

    const row = dataArr[rowIndex];
    const dataSetId = row?._savedDataSetId ?? savedDataSets[rowIndex]?.id;
    // Use sequential display number (rowIndex + 1) to match the "Data Set ID" column in the table
    const dataSetName = `Data Set ${rowIndex + 1}`;

    // Show confirmation dialog
    setDeleteDialog({
      open: true,
      rowIndex,
      dataSetName,
      dataSetId: dataSetId ?? undefined
    });
  };

  // Handle confirmed delete action
  const handleConfirmDelete = async () => {
    if (deleteDialog.rowIndex === null) return;

    const rowIndex = deleteDialog.rowIndex;
    const dataSetId = deleteDialog.dataSetId;

    if (onDelete) {
      // Call the parent's delete handler with row index and data set ID
      await onDelete(rowIndex, dataSetId);
    }

    // Update local state
    if (rowIndex < dataArr.length) {
      setDataArr(prev => {
        const newArr = prev.filter((_, i) => i !== rowIndex);
        if (requestId == null && onDataChange) onDataChange(newArr);
        return newArr;
      });
      
      if (selectedRowIdx === rowIndex) {
        setSelectedRowIdx(Math.max(0, rowIndex - 1));
      } else if (selectedRowIdx > rowIndex) {
        setSelectedRowIdx(selectedRowIdx - 1);
      }
    }

    // Close the dialog
    setDeleteDialog({
      open: false,
      rowIndex: null,
      dataSetName: '',
      dataSetId: undefined
    });
  };

  // Handle cancel delete action
  const handleCancelDelete = () => {
    setDeleteDialog({
      open: false,
      rowIndex: null,
      dataSetName: '',
      dataSetId: undefined
    });
  };

  const dataRowCount = dataArr.length;
  const toggleSelectForDelete = (idx: number) => {
    if (idx >= dataRowCount) return;
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  const selectAllForDelete = () => {
    if (dataRowCount === 0) return;
    setSelectedForDelete(new Set(Array.from({ length: dataRowCount }, (_, i) => i)));
  };
  const deselectAllForDelete = () => setSelectedForDelete(new Set());
  const handleBulkDeleteClick = () => {
    if (selectedForDelete.size === 0) return;
    setBulkDeleteConfirmOpen(true);
  };
  const handleBulkDeleteConfirm = async () => {
    if (!onDeleteMultiple || selectedForDelete.size === 0) return;
    const items = Array.from(selectedForDelete)
      .map(idx => {
        const row = dataArr[idx];
        const saved = idx < savedDataSets.length ? savedDataSets[idx] : null;
        const dataSetId = saved?.id ?? row?._savedDataSetId ?? undefined;
        return { rowIndex: idx, dataSetId };
      })
      .sort((a, b) => b.rowIndex - a.rowIndex);
    setBulkDeleteInProgress(true);
    try {
      await onDeleteMultiple(items);
      setSelectedForDelete(new Set());
      setBulkDeleteConfirmOpen(false);
      setSuccessSnackbar(`Deleted ${items.length} data set(s)`);
    } catch (err: any) {
      setError(extractApiError(err) || 'Failed to delete selected data sets');
    } finally {
      setBulkDeleteInProgress(false);
    }
  };
  const handleBulkDeleteCancel = () => {
    if (!bulkDeleteInProgress) setBulkDeleteConfirmOpen(false);
  };

  const renderCell = (flatRow: any, rowIdx: number, key: string) => {
    const val = flatRow[key];
    const templateType = requestBodyLeafTypesRef.current.get(key);
    if (Array.isArray(val) || templateType === 'array') {
      let arrayValue: unknown[] = [];
      if (Array.isArray(val)) {
        arrayValue = val;
      } else if (typeof val === 'string' && val.trim()) {
        const parsed = tryParseArrayCellValue(val);
        if (parsed != null) arrayValue = parsed;
      }
      return (
        <ArrayCellInput
          rowIdx={rowIdx}
          columnKey={key}
          value={arrayValue}
          onChange={handleCellChange}
          onEditStart={() => {
            isCellEditingRef.current = true;
          }}
          onEditEnd={() => {
            isCellEditingRef.current = false;
            isUpdatingFromCellEdit.current = false;
          }}
        />
      );
    }
    if (typeof val === 'object' && val !== null) {
      const strVal = JSON.stringify(val, null, 2);
      const displayVal = strVal.length > 20 ? strVal.slice(0, 20) + '...' : strVal;
      return (
        <Tooltip title={<pre style={{margin:0,whiteSpace:'pre-wrap'}}>{strVal}</pre>} arrow>
          <span style={{ display: 'block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayVal}</span>
        </Tooltip>
      );
    }
    const strVal = val === undefined ? '' : String(val);
    const editKey = cellEditKey(rowIdx, key);
    return (
      <Tooltip title={strVal} arrow>
        <input
          value={strVal}
          style={{ width: '100%', minWidth: 0, maxWidth: '100%', border: 'none', background: 'transparent', outline: 'none', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', boxSizing: 'border-box' }}
          onFocus={() => {
            isCellEditingRef.current = true;
            cellOriginalTypeRef.current.set(
              editKey,
              resolveCellOriginalType(key, val, requestBodyLeafTypesRef.current)
            );
          }}
          onBlur={() => {
            cellOriginalTypeRef.current.delete(editKey);
            isCellEditingRef.current = false;
            isUpdatingFromCellEdit.current = false;
          }}
          onChange={e => handleCellChange(rowIdx, key, e.target.value)}
        />
      </Tooltip>
    );
  };

  // In Data Sets mode, use current request body (value/editor/saved rows) as column source so table matches body shape.
  const requestBodyTextForColumns = resolveRequestBodyTextForColumns(
    value,
    editorValue,
    latestEditorValueRef?.current,
    allDataSets,
    rawBodySubtype
  );
  const mergedLeafTypes = mergeRequestBodyLeafTypes(
    collectRequestBodyTextCandidates(
      value,
      editorValue,
      latestEditorValueRef?.current,
      allDataSets
    )
  );
  mergedLeafTypes.forEach((type, key) => {
    const prev = stableRequestBodyLeafTypesRef.current.get(key);
    if (!prev || leafTypePriority(type) > leafTypePriority(prev)) {
      stableRequestBodyLeafTypesRef.current.set(key, type);
    }
  });
  requestBodyLeafTypesRef.current = stableRequestBodyLeafTypesRef.current;
  const requestBodyKeys = requestId ? getRequestBodyColumnKeys(requestBodyTextForColumns, rawBodySubtype) : [];
  const monacoBodyEditorKey = requestId != null ? `request-body-${requestId}` : 'request-body';
  const templateRowForKeys = requestId && requestBodyKeys.length > 0
    ? Object.fromEntries(
        requestBodyKeys.map((k) => {
          const t = stableRequestBodyLeafTypesRef.current.get(k);
          if (t === 'array') return [k, []];
          if (t === 'boolean') return [k, ''];
          if (t === 'null') return [k, null];
          return [k, ''];
        })
      )
    : Object.fromEntries(Object.keys(dataArr[0] || {}).map(k => [k, '']));
  // Compute all flattened keys from all rows (including template row), only include if never an object in any row
  const allKeysSet = new Set<string>();
  const keyIsObject = new Map<string, boolean>();
  const rowsForColumnKeys =
    requestId && requestBodyKeys.length > 0
      ? [templateRowForKeys]
      : dataArr.length > LARGE_DATA_SET_TABLE_THRESHOLD
        ? [dataArr[0], dataArr[dataArr.length - 1], templateRowForKeys].filter(Boolean)
        : [...dataArr, templateRowForKeys];
  rowsForColumnKeys.forEach(row => {
    const flat = getFlatRowForDisplay(row);
    for (const key of Object.keys(flat)) {
      allKeysSet.add(key);
      const val = flat[key];
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        keyIsObject.set(key, true);
      }
    }
  });
  const allFlattenedKeys = Array.from(allKeysSet).filter(
    key => !keyIsObject.get(key)
  );
  // Filter out only internal metadata fields. Do NOT filter by empty values:
  // columns must persist when values are cleared to keep table structure consistent.
  // When request body defines columns (Data Sets mode), use it so table shows only current body fields.
  const allFlattenedKeysFiltered = requestId && requestBodyKeys.length > 0
    ? requestBodyKeys
    : allFlattenedKeys.filter(
        key =>
          key !== '_savedDataSetId' &&
          key !== '_savedDataSetIndex' &&
          key !== '__lazyBody' &&
          key !== '__rawBody'
      );
  templateColumnKeysRef.current = allFlattenedKeysFiltered;

  const tableBodyColSpan =
    (requestId && onDeleteMultiple ? 1 : 0) + 1 + allFlattenedKeysFiltered.length + 3;

  const handleTableScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (useVirtualTableRows) {
      setTableScrollTop(e.currentTarget.scrollTop);
    }
  }, [useVirtualTableRows]);

  useEffect(() => {
    if (!useVirtualTableRows || !tableContainerInnerRef.current) return;
    const el = tableContainerInnerRef.current;
    const measure = () => setTableViewportHeight(el.clientHeight || 480);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [useVirtualTableRows, paginatedDataArr.length]);

  useEffect(() => {
    if (!useVirtualTableRows || !tableContainerInnerRef.current) return;
    const pageOffset = tablePage * tableRowsPerPage;
    const relativeIdx = selectedRowIdx - pageOffset;
    if (relativeIdx < 0 || relativeIdx >= tableRowsPerPage) return;
    const el = tableContainerInnerRef.current;
    const targetTop = Math.max(0, relativeIdx * VIRTUAL_ROW_HEIGHT_PX - VIRTUAL_ROW_HEIGHT_PX * 2);
    if (Math.abs(el.scrollTop - targetTop) > VIRTUAL_ROW_HEIGHT_PX) {
      el.scrollTop = targetTop;
      setTableScrollTop(targetTop);
    }
  }, [selectedRowIdx, useVirtualTableRows, paginatedDataArr.length, tablePage, tableRowsPerPage]);

  // Force table to fill container width
  useEffect(() => {
    let rafId: number | null = null;
    let lastAvailableWidth = 0;
    let pendingTimeout: NodeJS.Timeout | null = null;
    
    const forceTableWidth = () => {
      // Cancel any pending animation frame
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      // Use requestAnimationFrame to debounce and prevent ResizeObserver loops
      rafId = requestAnimationFrame(() => {
        if (tableRef.current && tableContainerInnerRef.current) {
          const table = tableRef.current;
          const container = tableContainerInnerRef.current;
          
          // Get available width (accounting for padding)
          const computed = window.getComputedStyle(container);
          const padding = parseInt(computed.paddingLeft || '0') + parseInt(computed.paddingRight || '0');
          const availableWidth = container.offsetWidth - padding;
          
          // Only update if width actually changed (prevents infinite loops)
          if (availableWidth > 0 && Math.abs(availableWidth - lastAvailableWidth) > 1) {
            lastAvailableWidth = availableWidth;
            
            // Get current computed styles to check if update is needed
            const currentTableWidth = parseFloat(table.style.width) || table.offsetWidth;
            
            // Only update if values actually changed (prevents ResizeObserver loops)
            if (Math.abs(currentTableWidth - availableWidth) > 1) {
              // Force container to be full width
              container.style.setProperty('width', '100%', 'important');
              container.style.setProperty('max-width', '100%', 'important');
              container.style.setProperty('min-width', '100%', 'important');
              
              // Force table width to match available width
              table.style.setProperty('width', `${availableWidth}px`, 'important');
              table.style.setProperty('min-width', `${availableWidth}px`, 'important');
              table.style.setProperty('max-width', `${availableWidth}px`, 'important');
              table.style.setProperty('table-layout', 'fixed', 'important');
            }
          }
        }
        rafId = null;
      });
    };

    forceTableWidth();
    const timeoutId1 = setTimeout(forceTableWidth, 50);
    const timeoutId2 = setTimeout(forceTableWidth, 200);
    const timeoutId3 = setTimeout(forceTableWidth, 500);
    const timeoutId4 = setTimeout(forceTableWidth, 1000);

    // Use requestAnimationFrame inside ResizeObserver to avoid feedback loops
    let resizeRaf: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeRaf !== null) {
        return;
      }
      resizeRaf = window.requestAnimationFrame(() => {
        resizeRaf = null;
        forceTableWidth();
      });
      // Clear any pending timeout
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
      }
      // Use a debounced callback to prevent loops
      pendingTimeout = setTimeout(() => {
        forceTableWidth();
        pendingTimeout = null;
      }, 10);
    });
    
    if (tableContainerInnerRef.current) {
      resizeObserver.observe(tableContainerInnerRef.current);
    }
    
    window.addEventListener('resize', forceTableWidth);
    
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (pendingTimeout) {
        clearTimeout(pendingTimeout);
      }
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(timeoutId4);
      if (resizeRaf !== null) {
        window.cancelAnimationFrame(resizeRaf);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', forceTableWidth);
    };
  }, [displayDataArr.length, allFlattenedKeysFiltered.length]);

  // Scroll table so Actions column (Execute icon) is visible by default (once when data first loads)
  const hasScrolledToActionsRef = useRef(false);
  useEffect(() => {
    if (displayDataArr.length === 0) return;
    if (hasScrolledToActionsRef.current) return;
    const scrollToShowActions = () => {
      const container = tableContainerInnerRef.current;
      if (!container) return;
      const maxScroll = container.scrollWidth - container.clientWidth;
      if (maxScroll > 0) {
        container.scrollLeft = maxScroll;
        hasScrolledToActionsRef.current = true;
      }
    };
    const t1 = setTimeout(scrollToShowActions, 100);
    const t2 = setTimeout(scrollToShowActions, 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [displayDataArr.length]);

  return (
    <Box sx={{ 
      width: '100%', 
      minWidth: 0, 
      maxWidth: '100%', 
      overflowX: 'auto',
      overflowY: 'visible',
      boxSizing: 'border-box'
    }}>
      {loading && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Loading data sets...
          </Typography>
        </Box>
      )}

      {beautifyPortalTarget
        ? createPortal(beautifyButton, beautifyPortalTarget)
        : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            {beautifyButton}
          </Box>
        )}

      <Paper sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', mb: 3 }}>
        <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto', overflowY: 'visible' }}>
          <MonacoEditorWithSearch
            key={`request-body-${requestBodyEditorKey}`}
            editorKey={monacoBodyEditorKey}
            defaultValue={editorValue}
            disableCompletions={rawBodySubtype === 'JSON'}
            allowFunctionPlaceholders={rawBodySubtype === 'JSON'}
            onEditorMount={(editor) => {
              monacoEditorRef.current = editor;
            }}
            onChange={handleEditorChange}
            language={getSyntaxHighlightingLanguage(rawBodySubtype)}
            height={200}
            autoHeight
            onFocus={handleEditorFocus}
            onBlur={handleEditorBlur}
            onCopy={handleCopyRequestBody}
            copyCopied={requestBodyCopied}
            searchPosition={searchContainerRef ? 'header' : 'overlay'}
            searchContainerRef={searchContainerRef}
          />
        </Box>
        {/* Fixed-height error strip: prevents layout shift when error appears/disappears */}
        <Box sx={{ minHeight: 40, mt: 1 }}>
          {error && (
            <Typography variant="body2" color="error" sx={{ display: 'block', fontSize: '0.95rem' }}>
              {error}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ 
        p: 2, 
        bgcolor: 'white', 
        borderRadius: 1, 
        border: '1px solid #e0e0e0', 
        width: '100%', 
        minWidth: 0, 
        maxWidth: '100%',
        overflowX: 'auto',
        overflowY: 'visible',
        display: 'flex', 
        flexDirection: 'column',
        boxSizing: 'border-box',
        minHeight: '500px', // Prevent height collapse during transitions
        transition: 'none' // Prevent smooth transitions
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ minWidth: 0, mb: 0 }}>
              Data Table
            </Typography>
            {/* Bulk execution indicator */}
            {isExecutingAllDataSets && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <CircularProgress size={18} thickness={5} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Executing {executionProgress.current}/{executionProgress.total} data sets...
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {requestId && savedDataSets.length > 0 && !loading && !isExecutingAllDataSets && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {savedDataSets.length} data set{savedDataSets.length !== 1 ? 's' : ''} loaded
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleAddDataSet}
              size="small"
            >
              Add Data Set
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportCSV}
              size="small"
              startIcon={<FileDownloadIcon />}
              disabled={dataArr.length === 0}
            >
              Export CSV
            </Button>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-import-input"
              type="file"
              onChange={handleImportCSV}
            />
            <label htmlFor="csv-import-input">
              <Button
                variant="outlined"
                component="span"
                size="small"
                startIcon={<FileUploadIcon />}
              >
                Import CSV
              </Button>
            </label>
            {requestId && onDeleteMultiple && selectedForDelete.size > 0 && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteSweepIcon />}
                  onClick={handleBulkDeleteClick}
                  disabled={bulkDeleteInProgress || isExecutingAllDataSets}
                >
                  Delete selected ({selectedForDelete.size})
                </Button>
                <Button variant="outlined" size="small" onClick={deselectAllForDelete} disabled={bulkDeleteInProgress}>
                  Deselect all
                </Button>
              </>
            )}
            {requestId && onDataSetsGenerated && (
              <>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setError('');
                    setBulkDialogOpen(true);
                  }}
                  size="small"
                  disabled={isExecutingAllDataSets}
                >
                  Add Data Sets Bulk
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setAiDialogOpen(true)}
                  size="small"
                  startIcon={<AutoAwesomeIcon />}
                  disabled={isExecutingAllDataSets}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653a8f 100%)',
                      border: 'none',
                    },
                  }}
                >
                  Add Data Sets With AI
                </Button>
              </>
            )}
          </Box>
        </Box>
        <Box 
          ref={tableContainerRef}
          className="data-table-wrapper"
          sx={{ 
            width: '100%', 
            minWidth: 0, 
            maxWidth: '100%', 
            overflowX: 'auto',
            overflowY: 'visible',
            flex: '0 0 auto',
            position: 'relative',
            boxSizing: 'border-box',
            minHeight: '400px', // Prevent height collapse
            transition: 'none' // Prevent smooth transitions that cause flicker
          }}
        >
          <TableContainer 
            component={Paper} 
            className="data-table-container"
            ref={tableContainerInnerRef}
            onScroll={handleTableScroll}
            sx={{ 
              boxShadow: 'none', 
              width: '100%',
              maxWidth: '100%',
              minWidth: 'max-content',
              minHeight: '400px', // Prevent height collapse
              overflowX: 'auto !important',
              overflowY: useVirtualTableRows ? 'auto !important' : 'visible',
              ...(useVirtualTableRows
                ? { maxHeight: 'min(520px, 55vh)' }
                : {}),
              display: 'block !important',
              position: 'relative',
              boxSizing: 'border-box',
              transition: 'none', // Prevent transitions that cause flicker
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555',
              },
              '& table': {
                width: 'max-content !important',
                minWidth: 'max-content !important',
                maxWidth: 'none !important',
                tableLayout: 'auto !important',
                display: 'table !important',
              },
              '& .MuiTableCell-head': {
                boxSizing: 'border-box !important',
              },
              '& .MuiTableCell-root': {
                boxSizing: 'border-box !important',
              }
            }}
          >
            <Table 
              ref={tableRef} 
              sx={{ 
                width: 'max-content !important', 
                minWidth: 'max-content !important', 
                maxWidth: 'none !important', 
                tableLayout: 'auto !important',
                display: 'table !important'
              }}
              style={{
                width: 'max-content',
                minWidth: 'max-content',
                maxWidth: 'none',
                tableLayout: 'auto',
                display: 'table'
              }}
            >
            
            {/* Removed colgroup to allow auto-sizing based on content */}
            <TableHead>
              <TableRow>
                {requestId && onDeleteMultiple && (
                  <TableCell padding="checkbox" sx={{
                    position: 'sticky',
                    left: 0,
                    backgroundColor: (theme) => theme.palette.background.paper ?? '#fff',
                    zIndex: 10,
                    boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                    width: 48,
                    minWidth: 48,
                    maxWidth: 48,
                  }}>
                    <Tooltip title={selectedForDelete.size === dataRowCount && dataRowCount > 0 ? 'Deselect all' : 'Select all'}>
                      <Checkbox
                        indeterminate={selectedForDelete.size > 0 && selectedForDelete.size < dataRowCount}
                        checked={dataRowCount > 0 && selectedForDelete.size === dataRowCount}
                        onChange={() => {
                          if (selectedForDelete.size === dataRowCount) deselectAllForDelete();
                          else selectAllForDelete();
                        }}
                        disabled={dataRowCount === 0}
                      />
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell sx={{ 
                  fontWeight: 600, 
                  whiteSpace: 'nowrap', 
                  minWidth: '100px !important', 
                  width: '100px !important',
                  maxWidth: '100px !important', 
                  position: 'sticky', 
                  left: requestId && onDeleteMultiple ? 48 : 0, 
                  backgroundColor: (theme) => theme.palette.background.paper ?? '#fff',
                  zIndex: 10, 
                  boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                  paddingLeft: 2,
                  paddingRight: 2,
                  boxSizing: 'border-box'
                }}>Data Set ID</TableCell>
                {allFlattenedKeysFiltered.map(key => {
                  // Calculate minimum width based on text length to prevent overlap
                  // Use 10px per character + 48px padding for better spacing
                  const minWidth = Math.max(150, key.length * 10 + 48);
                  return (
                    <TableCell 
                      key={key} 
                      sx={{ 
                        fontWeight: 600, 
                        whiteSpace: 'nowrap', 
                        minWidth: `${minWidth}px !important`, 
                        width: `${minWidth}px !important`,
                        maxWidth: 'none', 
                        overflow: 'visible', 
                        textOverflow: 'clip', 
                        paddingLeft: 3, 
                        paddingRight: 3,
                        boxSizing: 'border-box',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      {key}
                    </TableCell>
                  );
                })}
                <TableCell sx={{ 
                  fontWeight: 600, 
                  whiteSpace: 'nowrap', 
                  minWidth: '250px !important', 
                  width: '250px !important',
                  maxWidth: '250px !important', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  paddingLeft: 3, 
                  paddingRight: 3,
                  boxSizing: 'border-box'
                }}>Last Executed</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  whiteSpace: 'nowrap', 
                  minWidth: '100px !important', 
                  width: '100px !important',
                  maxWidth: '100px !important', 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis', 
                  paddingLeft: 3, 
                  paddingRight: 3,
                  boxSizing: 'border-box'
                }}>Status</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  whiteSpace: 'nowrap', 
                  minWidth: '240px !important', 
                  width: '240px !important',
                  maxWidth: 'none', 
                  overflow: 'visible', 
                  textOverflow: 'clip', 
                  paddingLeft: 3, 
                  paddingRight: 3,
                  position: 'sticky', 
                  right: 0, 
                  backgroundColor: (theme) => theme.palette.background.paper ?? '#fff',
                  zIndex: 3, 
                  boxShadow: '-2px 0 4px rgba(0,0,0,0.1)',
                  boxSizing: 'border-box',
                  verticalAlign: 'middle'
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {useVirtualTableRows && virtualRowWindow.topSpacer > 0 && (
                <TableRow aria-hidden>
                  <TableCell
                    colSpan={tableBodyColSpan}
                    sx={{ height: virtualRowWindow.topSpacer, padding: 0, border: 0, lineHeight: 0 }}
                  />
                </TableRow>
              )}
              {visibleTableRows.map((row, sliceIdx) => {
                const pageOffset = tablePage * tableRowsPerPage;
                const idx = pageOffset + (useVirtualTableRows ? virtualRowWindow.start + sliceIdx : sliceIdx);
                const flatRow = getFlatRowForDisplay(row);
                const rowDataSetId = row._savedDataSetId;
                const savedDataSet = rowDataSetId
                  ? savedDataSetsById.get(rowDataSetId) ?? (idx < savedDataSets.length ? savedDataSets[idx] : null)
                  : (idx < savedDataSets.length ? savedDataSets[idx] : null);
                // Display sequential number (1, 2, 3...) per request, not database ID
                const displayId = idx + 1;
                
                const isSelected = selectedRowIdx === idx;
                const isCurrentlyExecuting = currentlyExecutingDataSetId !== null && savedDataSet?.id === currentlyExecutingDataSetId;
                return (
                  <TableRow 
                    key={savedDataSet?.id ? `saved-${savedDataSet.id}` : `unsaved-${idx}`} 
                    hover 
                    sx={{
                      backgroundColor: isCurrentlyExecuting 
                        ? 'rgba(255, 152, 0, 0.15)' // Orange highlight for executing row
                        : isSelected 
                          ? 'rgba(25, 118, 210, 0.08)' 
                          : 'inherit',
                      '&:hover': {
                        backgroundColor: isCurrentlyExecuting
                          ? 'rgba(255, 152, 0, 0.25)'
                          : isSelected
                            ? 'rgba(25, 118, 210, 0.16)'
                            : 'action.hover',
                      },
                      transition: 'background-color 0.2s ease-out',
                      // Add subtle animation for currently executing row
                      ...(isCurrentlyExecuting && {
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%': { backgroundColor: 'rgba(255, 152, 0, 0.1)' },
                          '50%': { backgroundColor: 'rgba(255, 152, 0, 0.2)' },
                          '100%': { backgroundColor: 'rgba(255, 152, 0, 0.1)' },
                        },
                      }),
                    }}
                    onClick={() => {
                      setSelectedRowIdx(idx);
                      
                      isUserEditingUnsavedRow.current = false;
                      
                      // If this row corresponds to a saved data set, call onDataSetSelect
                      // Check savedDataSets first, then allDataSets (which includes newly created ones)
                      const rowDataSetId = row._savedDataSetId;
                      let effectiveSavedDataSet: RequestDataSet | null = null;
                      
                      if (rowDataSetId) {
                        effectiveSavedDataSet = savedDataSetsById.get(rowDataSetId) ?? null;
                        if (!effectiveSavedDataSet) {
                          effectiveSavedDataSet = allDataSetsById.get(rowDataSetId) ?? null;
                        }
                        
                        // If still not found, try savedDataSet (index-based fallback)
                        if (!effectiveSavedDataSet && savedDataSet) {
                          effectiveSavedDataSet = savedDataSet;
                        }
                      } else {
                        // No rowDataSetId, use index-based savedDataSet
                        effectiveSavedDataSet = savedDataSet || null;
                      }
                      
                      // Now that flag is reset, check if we should select this data set
                      if (effectiveSavedDataSet && effectiveSavedDataSet.id && onDataSetSelect) {
                        onDataSetSelect(effectiveSavedDataSet);
                      } else {
                        // Clear selected data set if clicking on an unsaved row
                        // This prevents showing assertions from the previous data set
                        if (onDataSetSelect) {
                          // Pass a data set with no ID to signal clearing the selection
                          const emptyDataSet: any = { id: undefined };
                          onDataSetSelect(emptyDataSet);
                        }
                      }
                    }}
                  >
                    {requestId && onDeleteMultiple && (
                      <TableCell padding="checkbox" sx={{
                        position: 'sticky',
                        left: 0,
                        backgroundColor: isCurrentlyExecuting
                          ? '#fff3e0'
                          : isSelected
                            ? '#e3f2fd'
                            : (theme) => theme.palette.background.paper ?? '#fff',
                        zIndex: 9,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                        width: 48,
                        minWidth: 48,
                        maxWidth: 48,
                      }} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedForDelete.has(idx)}
                          onChange={() => toggleSelectForDelete(idx)}
                          disabled={idx >= dataRowCount}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ 
                      fontWeight: 500, 
                      whiteSpace: 'nowrap', 
                      minWidth: '100px !important', 
                      width: '100px !important',
                      maxWidth: '100px !important', 
                      position: 'sticky', 
                      left: requestId && onDeleteMultiple ? 48 : 0, 
                      backgroundColor: isCurrentlyExecuting 
                        ? '#fff3e0' 
                        : isSelected 
                          ? '#e3f2fd' 
                          : (theme) => theme.palette.background.paper ?? '#fff',
                      zIndex: 9, 
                      boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                      paddingLeft: 2,
                      paddingRight: 2,
                      boxSizing: 'border-box',
                      transition: 'background-color 0.2s ease-out',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isCurrentlyExecuting && (
                          <CircularProgress size={14} thickness={5} sx={{ color: 'warning.main' }} />
                        )}
                        {displayId}
                      </Box>
                    </TableCell>
                    {allFlattenedKeysFiltered.map(key => {
                      // Calculate minimum width based on column name length to match header
                      // Use 10px per character + 48px padding for better spacing
                      const minWidth = Math.max(150, key.length * 10 + 48);
                      return (
                        <TableCell 
                          key={key} 
                          sx={{ 
                            whiteSpace: 'nowrap', 
                            minWidth: `${minWidth}px !important`, 
                            width: `${minWidth}px !important`,
                            maxWidth: 'none', 
                            overflow: 'visible', 
                            textOverflow: 'clip', 
                            padding: '12px',
                            boxSizing: 'border-box',
                            position: 'relative',
                            zIndex: 1,
                            backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                            transition: 'background-color 0.1s ease-out',
                          }}
                        >
                          {renderCell(flatRow, idx, key)}
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap', 
                      minWidth: '250px !important', 
                      width: '250px !important',
                      maxWidth: '250px !important', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      padding: '12px',
                      boxSizing: 'border-box',
                      backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                      transition: 'background-color 0.1s ease-out',
                    }}>
                      {(() => {
                        const rawId = row._savedDataSetId ?? savedDataSet?.id;
                        const dataSetId = rawId != null && Number(rawId) > 0 ? Number(rawId) : null;
                        const execResult = dataSetId != null
                          ? (executionResultsByDataSetId?.[dataSetId] ?? executionResults.get(dataSetId))
                          : null;
                        if (!execResult?.lastExecuted) return '--';
                        const formatted = formatLastExecutedTimestamp(execResult.lastExecuted);
                        return formatted === '—' ? '--' : formatted;
                      })()}
                    </TableCell>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap', 
                      minWidth: '100px !important', 
                      width: '100px !important',
                      maxWidth: '100px !important', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      padding: '12px',
                      boxSizing: 'border-box',
                      backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                      transition: 'background-color 0.1s ease-out',
                    }}>
                      {(() => {
                        const rawId = row._savedDataSetId ?? savedDataSet?.id;
                        const dataSetId = rawId != null && Number(rawId) > 0 ? Number(rawId) : null;
                        const execResult = dataSetId != null
                          ? (executionResultsByDataSetId?.[dataSetId] ?? executionResults.get(dataSetId))
                          : null;
                        const status = execResult?.status || 'Pending';
                        const color = status === 'Success' ? 'green' : status === 'Failed' ? 'red' : 'inherit';
                        return <span style={{ color, fontWeight: status !== 'Pending' ? 'bold' : 'normal' }}>{status}</span>;
                      })()}
                    </TableCell>
                    <TableCell sx={{ 
                      whiteSpace: 'nowrap', 
                      minWidth: '240px !important', 
                      width: '240px !important',
                      maxWidth: 'none', 
                      overflow: 'visible', 
                      textOverflow: 'clip', 
                      padding: '12px',
                      position: 'sticky', 
                      right: 0, 
                      backgroundColor: isSelected ? '#e3f2fd' : (theme) => theme.palette.background.paper ?? '#fff',
                      zIndex: 2, 
                      boxShadow: '-2px 0 4px rgba(0,0,0,0.1)',
                      boxSizing: 'border-box',
                      transition: 'background-color 0.1s ease-out',
                      verticalAlign: 'middle',
                    }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', alignContent: 'center', gap: 0.25, flexWrap: 'nowrap', lineHeight: 0, '& .MuiIconButton-root': { margin: 0, flexShrink: 0 }, '& > *': { display: 'inline-flex', alignItems: 'center' }, '& span': { display: 'inline-flex', alignItems: 'center' } }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={requestId ? "Save to database" : "Please select a request first"}>
                          <span>
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!onSave || isSavingRow) {
                                  return;
                                }
                                try {
                                  setIsSavingRow(true);
                                  // Table row is the source of truth for cell edits; editor sync is async
                                  // and can still hold stale flat JSON that would erase nested arrays.
                                  // Prefer dataArrRef so blur→Save in the same click uses the committed cell value.
                                  const rowToSave: any = dataArrRef.current[idx] ?? row;
                                  await Promise.resolve(onSave(rowToSave, idx));
                                } finally {
                                  setIsSavingRow(false);
                                }
                              }}
                              disabled={
                                // Disable if no request selected
                                !requestId || 
                                // Disable if it's the blank template row
                                idx >= dataArr.length ||
                                // Disable if row is not selected
                                selectedRowIdx !== idx ||
                                // Disable while a save is in progress to prevent duplicates
                                isSavingRow
                              }
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Duplicate Row">
                          <span>
                            <IconButton 
                              size="small" 
                              color="secondary" 
                              aria-label={`duplicate-data-set-${savedDataSet?.id || row._savedDataSetId || idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicate(idx, row);
                              }}
                              disabled={
                                // Disable if it's the blank template row
                                idx >= dataArr.length
                              }
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="View Details (Vertical)">
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRowIdx(idx);
                                const rowDataSetId = row._savedDataSetId;
                                let effectiveSavedDataSet: RequestDataSet | null = null;
                                if (rowDataSetId) {
                                  effectiveSavedDataSet =
                                    savedDataSetsById.get(rowDataSetId) ??
                                    allDataSetsById.get(rowDataSetId) ??
                                    null;
                                  if (!effectiveSavedDataSet && savedDataSet) {
                                    effectiveSavedDataSet = savedDataSet;
                                  }
                                } else if (savedDataSet) {
                                  effectiveSavedDataSet = savedDataSet;
                                }
                                if (effectiveSavedDataSet?.id && onDataSetSelect) {
                                  onDataSetSelect(effectiveSavedDataSet);
                                }
                                setVerticalViewRow(row);
                                setVerticalViewRowIndex(idx);
                                setVerticalViewOpen(true);
                              }}
                              disabled={idx >= dataArr.length}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(idx);
                              }}
                              disabled={
                                // Disable if it's the blank template row
                                idx >= dataArr.length ||
                                // Disable if row is not selected
                                selectedRowIdx !== idx
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {(onExecute || onExecuteDataSetId) && (
                        <Tooltip title="Execute">
                          <span>
                            <IconButton 
                              size="small" 
                              color="primary" 
                              aria-label={`execute-data-set-${savedDataSet?.id || row._savedDataSetId || idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                const dataSetId = (savedDataSet?.id as any) || row._savedDataSetId;
                                if (typeof dataSetId === 'number' && onExecuteDataSetId) {
                                  onExecuteDataSetId(dataSetId);
                                  return;
                                }
                                if (onExecute) {
                                  const cleanedRow = cleanDataForDisplay(row);
                                  onExecute(cleanedRow, idx);
                                }
                              }}
                              disabled={
                                // Disable if it's the blank template row
                                idx >= dataArr.length ||
                                // Disable if row is not selected
                                selectedRowIdx !== idx
                              }
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {useVirtualTableRows && virtualRowWindow.bottomSpacer > 0 && (
                <TableRow aria-hidden>
                  <TableCell
                    colSpan={tableBodyColSpan}
                    sx={{ height: virtualRowWindow.bottomSpacer, padding: 0, border: 0, lineHeight: 0 }}
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {displayDataArr.length > 0 && (
          <TablePagination
            component="div"
            count={displayDataArr.length}
            page={tablePage}
            onPageChange={(_, newPage) => setTablePage(newPage)}
            rowsPerPage={tableRowsPerPage}
            onRowsPerPageChange={(e) => {
              setTableRowsPerPage(parseInt(e.target.value, 10));
              setTablePage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
        </Box>
      </Paper>

      {/* Render the vertical view modal at the root, not inside the table */}
      <VerticalViewModal
        open={verticalViewOpen}
        onClose={() => {
          setVerticalViewOpen(false);
          setVerticalViewRowIndex(null);
        }}
        rowData={verticalViewRow ? getFlatRowForDisplay(verticalViewRow) : {}}
        rowIndex={
          verticalViewRow
            ? resolveDataArrRowIndex(displayDataArr, verticalViewRow, verticalViewRowIndex)
            : undefined
        }
        onSave={async (updatedRow) => {
          const idx = resolveDataArrRowIndex(displayDataArr, verticalViewRow, verticalViewRowIndex);
          if (idx < 0 || !onSave) {
            throw new Error('Could not find data set row to save');
          }
          const rowToSave = buildRowFromModalSave(
            verticalViewRow,
            updatedRow,
            requestBodyLeafTypesRef.current
          );
          await (onSave as (a: any, b: number, c?: { fromModal?: boolean }) => Promise<void>)(
            rowToSave,
            idx,
            { fromModal: true }
          );
          setDataArr((prev) => {
            const next = [...prev];
            if (idx < next.length) {
              next[idx] = rowToSave;
              if (onDataChange) onDataChange(next);
            }
            return next;
          });
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={{ sx: { width: 400, maxWidth: '90vw' } }}
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.dataSetName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteConfirmOpen}
        onClose={handleBulkDeleteCancel}
        aria-labelledby="bulk-delete-dialog-title"
        PaperProps={{ sx: { width: 400, maxWidth: '90vw' } }}
      >
        <DialogTitle id="bulk-delete-dialog-title">
          Delete {selectedForDelete.size} data set(s)?
        </DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete the selected data set(s). This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDeleteCancel} color="primary" disabled={bulkDeleteInProgress}>
            Cancel
          </Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" variant="contained" disabled={bulkDeleteInProgress} startIcon={bulkDeleteInProgress ? <CircularProgress size={16} color="inherit" /> : null}>
            {bulkDeleteInProgress ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Data Sets Bulk Dialog - Original design */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => !bulkLoading && (setBulkDialogOpen(false), setBulkCount('10'), setError(''))}
        maxWidth="xs"
        fullWidth={false}
        PaperProps={{
          sx: {
            width: '400px',
            maxWidth: '400px',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontSize: '1.1rem' }}>Generate Bulk Data Sets</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
            Generate synthetic data sets based on your request body and schema.
          </Typography>
          {bulkLoading && bulkProgress && (
            <Typography variant="body2" color="primary" sx={{ mb: 1, fontSize: '0.8rem' }}>
              Generating {bulkProgress.created}/{bulkProgress.requested} data sets...
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Number of Data Sets"
            type="text"
            inputMode="numeric"
            fullWidth
            variant="outlined"
            size="small"
            value={bulkCount}
            onChange={(e) => handleBulkCountChange(e.target.value)}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = e.clipboardData.getData('text');
              handleBulkCountChange(pasted);
            }}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onWheel={(e) => {
              (e.target as HTMLElement).blur();
            }}
            disabled={bulkLoading}
            error={bulkCount !== '' && !bulkCountReady}
            helperText={
              bulkCount !== '' && !bulkCountReady
                ? `Enter ${MIN_BULK_GENERATE_COUNT}–${MAX_BULK_GENERATE_COUNT} to generate`
                : `Between ${MIN_BULK_GENERATE_COUNT} and ${MAX_BULK_GENERATE_COUNT}`
            }
            inputProps={{
              min: 0,
              max: MAX_BULK_GENERATE_COUNT,
              pattern: '[0-9]*',
              'aria-valuemin': 0,
              'aria-valuemax': MAX_BULK_GENERATE_COUNT,
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
          <Button onClick={() => { setBulkDialogOpen(false); setBulkCount('10'); setError(''); }} disabled={bulkLoading} size="small">
            Cancel
          </Button>
          <Button
            onClick={handleBulkGenerate}
            variant="contained"
            disabled={bulkLoading || !bulkCountReady}
            startIcon={bulkLoading ? <CircularProgress size={16} color="inherit" /> : null}
            size="small"
          >
            {bulkLoading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Data Sets with AI Dialog - Original design */}
      <Dialog
        open={aiDialogOpen}
        onClose={() => !aiLoading && (setAiDialogOpen(false), setAiPrompt(''), setAiPositiveCount('3'), setAiNegativeCount('2'))}
        maxWidth="md"
        fullWidth={false}
        PaperProps={{
          sx: {
            width: '600px',
            maxWidth: '600px',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5, fontSize: '1.2rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          Generate Data Sets With AI
        </DialogTitle>
        <DialogContent sx={{ pt: 3, ...environmentPlaceholderSx }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" component="div">
              Enter a prompt describing your test data requirements. The AI will generate both positive and negative
              test cases based on your request body and schema.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              For large runs (e.g. 20+20), generation happens in batches and may take a few minutes. If some rows are
              missing, run again with the same prompt to fill the gap, or use smaller counts per run.
            </Typography>
          </Alert>

          <TextField
            autoFocus
            margin="dense"
            label="Prompt"
            placeholder="e.g., Generate test data for user registration with various email formats, age ranges, and password strengths. Include edge cases for validation."
            fullWidth
            multiline
            rows={5}
            variant="outlined"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            disabled={aiLoading}
            sx={{ mb: 3 }}
            helperText="Describe what kind of test data you need. The AI will use your request body and schema to generate appropriate test cases."
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              margin="dense"
              label="Positive Test Cases"
              type="number"
              fullWidth
              variant="outlined"
              value={aiPositiveCount}
              onChange={(e) => setAiPositiveCount(clampAiTestCaseCountInput(e.target.value))}
              disabled={aiLoading}
              helperText="Valid test cases (0-20)"
              inputProps={{ min: 0, max: MAX_AI_TEST_CASE_COUNT }}
            />
            <TextField
              margin="dense"
              label="Negative Test Cases"
              type="number"
              fullWidth
              variant="outlined"
              value={aiNegativeCount}
              onChange={(e) => setAiNegativeCount(clampAiTestCaseCountInput(e.target.value))}
              disabled={aiLoading}
              helperText="Invalid/edge cases (0-20)"
              inputProps={{ min: 0, max: MAX_AI_TEST_CASE_COUNT }}
            />
          </Box>

          {aiLoading && aiProgress && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
              {aiProgress.message
                ? `${aiProgress.message} (${aiProgress.created}/${aiProgress.requested})`
                : `Generating ${aiProgress.created}/${aiProgress.requested} data sets…`}
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => {
              setAiDialogOpen(false);
              setError('');
              setAiPrompt('');
              setAiPositiveCount('3');
              setAiNegativeCount('2');
            }}
            sx={{ mr: 1 }}
            disabled={aiLoading}
            size="medium"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAiGenerate}
            variant="contained"
            disabled={
              aiLoading ||
              !aiPrompt.trim() ||
              ((aiPositiveCount === '' ? 0 : parseInt(aiPositiveCount, 10)) === 0 &&
                (aiNegativeCount === '' ? 0 : parseInt(aiNegativeCount, 10)) === 0)
            }
            startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            size="medium"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8f 100%)',
              },
            }}
          >
            {aiLoading
              ? aiProgress
                ? `Generating ${aiProgress.created}/${aiProgress.requested}…`
                : 'Generating with AI...'
              : 'Generate with AI'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successSnackbar}
        autoHideDuration={4000}
        onClose={() => setSuccessSnackbar(null)}
        message={successSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default RawJsonTable; 